import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import {
  formatKenyanPhoneDisplay,
  formatMpesaPhoneNumber,
  mpesaPhoneValidationMessage,
  sanitizePhoneInput
} from './utils/mpesaPhone';
import {
  buildDonationCheckout,
  needAmountRemaining
} from './utils/donationBreakdown';
import PasswordInput from './PasswordInput';
import {
  HELP_SECTIONS,
  SUPPORT_PHONE_DISPLAY,
  SUPPORT_TEL_URL,
  SUPPORT_WHATSAPP_URL
} from './helpContent';
import RemindersTab from './RemindersTab';
import { APP_VERSION_LABEL } from './version';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const TOKEN_KEY = 'donorTrack_token';
const BOOKMARKS_KEY = 'donorTrack_bookmarked_beneficiaries';
const RESET_EMAIL_KEY = 'donorTrack_reset_email';
const DONATE_QUICK_AMOUNTS = [500, 1000, 2500, 5000, 10000];
const DONORTRACK_SUPPORT_CHIPS = [
  { id: 'none', label: 'No thanks' },
  { id: '10', label: 'Ksh 10' },
  { id: '25', label: 'Ksh 25' },
  { id: '50', label: 'Ksh 50' },
  { id: 'custom', label: 'Custom' }
];

const PAYMENT_METHOD_OPTIONS = [
  { id: 'Mpesa', label: 'M-Pesa', shortLabel: 'M-Pesa' },
  { id: 'Airtel', label: 'Airtel Money', shortLabel: 'Airtel' },
  { id: 'Card', label: 'Card', shortLabel: 'Card' }
];

function PaymentMethodMark({ methodId }) {
  if (methodId === 'Mpesa') {
    return (
      <svg className="payment-method-card__svg" viewBox="0 0 32 32" aria-hidden="true">
        <rect width="32" height="32" rx="8" fill="#e8f5ee" />
        <path
          d="M10 11h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2Z"
          fill="none"
          stroke="#2d6a4f"
          strokeWidth="1.5"
        />
        <path d="M8 14h16" stroke="#2d6a4f" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="22" cy="20" r="1.5" fill="#40916c" />
      </svg>
    );
  }
  if (methodId === 'Airtel') {
    return (
      <svg className="payment-method-card__svg" viewBox="0 0 32 32" aria-hidden="true">
        <rect width="32" height="32" rx="8" fill="#fdeeee" />
        <path
          d="M16 8c-3.5 0-6 2.2-6 5.5 0 2.2 1.4 3.5 3.2 4.8 1.5 1.1 2.8 2 2.8 3.7 0 1.8-1.5 3-3.5 3.5"
          fill="none"
          stroke="#c0392b"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
        <path d="M16 8v16" stroke="#e74c3c" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg className="payment-method-card__svg" viewBox="0 0 32 32" aria-hidden="true">
      <rect width="32" height="32" rx="8" fill="#eef2f0" />
      <rect x="7" y="11" width="18" height="12" rx="2" fill="none" stroke="#52796f" strokeWidth="1.5" />
      <rect x="9" y="15" width="5" height="3" rx="0.5" fill="#2d6a4f" opacity="0.85" />
      <path d="M17 17h6" stroke="#95a5a6" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function needEffectiveGoal(need) {
  return Number(
    need?.totalGoalAmount ||
      Number(need?.goalAmount || 0) + Number(need?.processingFeeAmount || 0)
  );
}

function isNeedFullyFunded(need) {
  const goal = needEffectiveGoal(need);
  const raised = Number(need?.raisedAmount || 0);
  return goal > 0 && raised >= goal;
}

/** True when every listed need has reached its goal (or there are no open needs). */
function isBeneficiaryFullyFunded(beneficiary) {
  const needs = beneficiary?.needs || [];
  if (!needs.length) return true;
  return needs.every(isNeedFullyFunded);
}

function needFundingProgress(need) {
  const goal = needEffectiveGoal(need);
  return Math.min(100, (Number(need?.raisedAmount || 0) / Math.max(goal, 1)) * 100);
}

function flattenBeneficiaryNeeds(beneficiaryRows) {
  const entries = [];
  for (const beneficiary of beneficiaryRows) {
    for (const need of beneficiary.needs || []) {
      entries.push({ beneficiary, need });
    }
  }
  return entries;
}

function filterNeedEntriesForDisplay(entries, query) {
  const q = String(query || '')
    .trim()
    .toLowerCase();
  if (!q) return entries;
  return entries.filter(({ beneficiary, need }) => {
    const hay = [beneficiary.name, beneficiary.location, need.category, need.description]
      .map((v) => String(v || '').toLowerCase())
      .join(' ');
    return hay.includes(q);
  });
}

function needShareKey(beneficiaryId, needId) {
  return `${String(beneficiaryId)}:${String(needId)}`;
}

function storyPreviewText(html) {
  if (!html) return '';
  if (typeof document !== 'undefined') {
    const tmp = document.createElement('div');
    tmp.innerHTML = String(html);
    return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
  }
  return String(html)
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function countNeedsByCategory(beneficiaryRows) {
  const counts = { All: 0 };
  for (const b of beneficiaryRows) {
    for (const need of b.needs || []) {
      counts.All += 1;
      const cat = need.category || 'Other';
      counts[cat] = (counts[cat] || 0) + 1;
    }
  }
  return counts;
}

function donationStatusLabel(status) {
  const s = String(status || 'pending').toLowerCase();
  if (s === 'completed') return 'Completed';
  if (s === 'failed') return 'Failed';
  if (s === 'refunded') return 'Refunded';
  return 'Pending';
}

function donationMatchesStatusFilter(donation, statusFilter) {
  if (statusFilter === 'all') return true;
  const s = String(donation?.status || 'pending').toLowerCase();
  if (statusFilter === 'failed') return s === 'failed' || s === 'refunded';
  return s === statusFilter;
}

function filterMyDonationsForDisplay(rows, query, statusFilter) {
  let out = rows.filter((d) => donationMatchesStatusFilter(d, statusFilter));
  const q = String(query || '')
    .trim()
    .toLowerCase();
  if (!q) return out;
  return out.filter((d) => {
    const b = d.Beneficiary || d.beneficiary || {};
    const hay = [
      b.name,
      b.category,
      b.location,
      parseDonationPaymentMethod(d),
      d.status,
      donationStatusLabel(d.status),
      String(d.amount ?? '')
    ]
      .map((v) => String(v || '').toLowerCase())
      .join(' ');
    return hay.includes(q);
  });
}

function countMyDonationsByStatus(rows) {
  const counts = { all: rows.length, completed: 0, pending: 0, failed: 0 };
  for (const row of rows) {
    const s = String(row.status || 'pending').toLowerCase();
    if (s === 'completed') counts.completed += 1;
    else if (s === 'failed' || s === 'refunded') counts.failed += 1;
    else counts.pending += 1;
  }
  return counts;
}

function formatPaymentMethodDisplay(method) {
  const raw = String(method || '').trim();
  if (!raw || raw === '—') return '—';
  if (raw.toLowerCase() === 'mpesa') return 'M-Pesa';
  return raw;
}

function filterPaymentUpdatesForDisplay(rows, query) {
  const q = String(query || '')
    .trim()
    .toLowerCase();
  if (!q) return rows;
  return rows.filter((u) => {
    const hay = [
      u.beneficiaryName,
      u.category,
      u.beneficiaryLocation,
      u.description,
      formatPaymentMethodLabel(u.paymentMethod),
      u.paymentDetails,
      u.receiptFileName
    ]
      .map((v) => String(v || '').toLowerCase())
      .join(' ');
    return hay.includes(q);
  });
}

function pruneFullyFundedBookmarks(bookmarkIds, beneficiaries) {
  if (!bookmarkIds.length) return bookmarkIds;
  const byId = new Map(beneficiaries.map((b) => [String(b.id), b]));
  return bookmarkIds.filter((id) => {
    const beneficiary = byId.get(String(id));
    if (!beneficiary) return false;
    return !isBeneficiaryFullyFunded(beneficiary);
  });
}

function bookmarkIdsEqual(a, b) {
  if (a.length !== b.length) return false;
  return a.every((id, index) => String(id) === String(b[index]));
}
function readStoredResetEmail() {
  try {
    return localStorage.getItem(RESET_EMAIL_KEY) || '';
  } catch {
    return '';
  }
}

function writeStoredResetEmail(email) {
  try {
    if (email) localStorage.setItem(RESET_EMAIL_KEY, String(email).trim().toLowerCase());
    else localStorage.removeItem(RESET_EMAIL_KEY);
  } catch (_) {}
}

function formatCurrency(value) {
  return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(Number(value || 0));
}

function formatDate(value) {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString();
}

function parseDonationPaymentMethod(donation) {
  if (donation?.paymentMethod) return donation.paymentMethod;
  const match = String(donation?.message || '').match(/^\[([^\]]+)\]/);
  return match ? match[1] : '—';
}

function donationStatusBadgeClass(status) {
  if (status === 'completed') return 'status-paid';
  if (status === 'failed') return 'status-refunded';
  if (status === 'pending') return 'status-escrow';
  return 'status-escrow';
}

function formatPaymentMethodLabel(method) {
  const key = String(method || '').trim().toLowerCase();
  if (key === 'bank_transfer') return 'Bank transfer';
  if (key === 'mobile_money') return 'Mobile money';
  if (key === 'cash') return 'Cash';
  if (key === 'other') return 'Other';
  return method || '—';
}

function downloadReceiptFile({ fileName, mimeType, contentBase64 }) {
  const name = String(fileName || 'receipt').trim() || 'receipt';
  const type = String(mimeType || 'application/octet-stream').trim() || 'application/octet-stream';
  const b64 = String(contentBase64 || '').trim();
  if (!b64) return;
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function PaymentReceiptAttachment({ update }) {
  const fileName = update?.receiptFileName;
  const mimeType = update?.receiptMimeType;
  const contentBase64 = update?.receiptContentBase64;
  if (!fileName || !contentBase64) return null;
  const isImage = String(mimeType || '').startsWith('image/');
  const dataUrl = `data:${mimeType || 'application/octet-stream'};base64,${contentBase64}`;

  return (
    <div className="update-receipt">
      <span className="update-receipt-label">Receipt</span>
      <div className="update-receipt-row">
        {isImage ? (
          <a
            className="update-receipt-preview-link"
            href={dataUrl}
            download={fileName}
            title={`Download ${fileName}`}
          >
            <img className="update-receipt-preview" src={dataUrl} alt="" />
          </a>
        ) : null}
        <button
          type="button"
          className="btn btn-outline btn-sm update-receipt-download"
          onClick={() => downloadReceiptFile({ fileName, mimeType, contentBase64 })}
        >
          Download receipt
        </button>
      </div>
    </div>
  );
}

function PaymentDetailsTable({ summary }) {
  if (!summary) return null;
  const rows = [
    { label: 'Beneficiary', value: summary.beneficiaryName || '—' },
    { label: 'Location', value: summary.beneficiaryLocation || '—' },
    { label: 'Category', value: summary.category || '—' },
    { label: 'Request', value: summary.description || '—', html: Boolean(summary.description) },
    { label: 'Total goal', value: formatCurrency(summary.totalGoalAmount) },
    { label: 'Payment method', value: formatPaymentMethodLabel(summary.paymentMethod) },
    { label: 'Payment details', value: summary.paymentDetails || '—' },
    { label: 'Paid', value: formatDate(summary.paidAt) },
    { label: 'Closed', value: formatDate(summary.closedAt) }
  ];
  return (
    <div className="updates-detail-panel">
      <dl className="updates-detail-grid">
        {rows.map((row) => (
          <div className="updates-detail-grid__row" key={row.label}>
            <dt>{row.label}</dt>
            {row.html ? (
              <dd dangerouslySetInnerHTML={{ __html: row.value }} />
            ) : (
              <dd>{row.value}</dd>
            )}
          </div>
        ))}
      </dl>
      <PaymentReceiptAttachment update={summary} />
    </div>
  );
}

function PaymentUpdateDetails({ update }) {
  const rows = [
    update.beneficiaryLocation ? { label: 'Location', value: update.beneficiaryLocation } : null,
    update.description ? { label: 'Request', value: update.description, html: true } : null,
    { label: 'Total goal', value: formatCurrency(update.totalGoalAmount) },
    { label: 'Payment method', value: formatPaymentMethodLabel(update.paymentMethod) },
    { label: 'Payment details', value: update.paymentDetails || '—' },
    { label: 'Paid', value: formatDate(update.paidAt) },
    { label: 'Closed', value: formatDate(update.closedAt) }
  ].filter(Boolean);

  return (
    <div className="updates-detail-panel">
      <dl className="updates-detail-grid">
        {rows.map((row) => (
          <div className="updates-detail-grid__row" key={row.label}>
            <dt>{row.label}</dt>
            {row.html ? (
              <dd dangerouslySetInnerHTML={{ __html: row.value }} />
            ) : (
              <dd>{row.value}</dd>
            )}
          </div>
        ))}
      </dl>
      <PaymentReceiptAttachment update={update} />
    </div>
  );
}

function PaymentUpdatesList({ updates }) {
  const [expandedId, setExpandedId] = useState(() =>
    updates.length === 1 ? updates[0]?.id || null : null
  );

  useEffect(() => {
    if (updates.length === 1 && updates[0]?.id) {
      setExpandedId(updates[0].id);
    }
  }, [updates]);

  function toggleRow(id) {
    setExpandedId((current) => (current === id ? null : id));
  }

  return (
    <ul className="updates-list">
      {updates.map((update) => {
        const open = expandedId === update.id;
        const panelId = `update-panel-${update.id}`;
        const hasReceipt = Boolean(update.receiptFileName && update.receiptContentBase64);
        const meta = [update.category, update.beneficiaryLocation].filter(Boolean).join(' · ');
        return (
          <li key={update.id}>
            <article className={`updates-card${open ? ' updates-card--open' : ''}`}>
              <button
                type="button"
                className="updates-card__summary"
                onClick={() => toggleRow(update.id)}
                aria-expanded={open}
                aria-controls={panelId}
              >
                <div className="updates-card__identity">
                  <h3 className="updates-card__name">{update.beneficiaryName || 'Request'}</h3>
                  {meta ? <p className="updates-card__meta muted">{meta}</p> : null}
                </div>
                <div className="updates-card__aside">
                  <span className="updates-card__method">
                    {formatPaymentMethodLabel(update.paymentMethod)}
                  </span>
                  <time className="updates-card__date" dateTime={update.closedAt || ''}>
                    Closed {formatDate(update.closedAt)}
                  </time>
                  {hasReceipt ? (
                    <span className="updates-card__receipt-badge">Receipt</span>
                  ) : null}
                </div>
                <span
                  className={`update-list-chevron${open ? ' update-list-chevron--open' : ''}`}
                  aria-hidden
                >
                  ▾
                </span>
              </button>
              {open ? (
                <div className="updates-card__body" id={panelId}>
                  <p className="updates-card__verified muted">
                    Verified payment record for a request you supported.
                  </p>
                  <PaymentUpdateDetails update={update} />
                </div>
              ) : null}
            </article>
          </li>
        );
      })}
    </ul>
  );
}

function HelpFab({ onClick }) {
  return (
    <button type="button" className="help-fab" aria-label="Help" title="Help" onClick={onClick}>
      <span className="help-fab__mark" aria-hidden>
        ?
      </span>
    </button>
  );
}

function ContactHelpContent() {
  const contact = HELP_SECTIONS.contact;
  return (
    <div className="help-section-content">
      {contact.paragraphs.map((paragraph) => (
        <p key={paragraph.slice(0, 48)}>{paragraph}</p>
      ))}
      <div className="help-contact-actions">
        <a
          className="help-contact-action help-contact-action--whatsapp"
          href={SUPPORT_WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`WhatsApp ${SUPPORT_PHONE_DISPLAY}`}
        >
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
            <path
              fill="currentColor"
              d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"
            />
          </svg>
          <span>WhatsApp</span>
        </a>
        <a
          className="help-contact-action help-contact-action--call"
          href={SUPPORT_TEL_URL}
          aria-label={`Call ${SUPPORT_PHONE_DISPLAY}`}
        >
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
            <path
              fill="currentColor"
              d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.01-.24 11.36 11.36 0 0 0 3.56.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1 11.36 11.36 0 0 0 .57 3.56 1 1 0 0 1-.25 1.01l-2.2 2.22Z"
            />
          </svg>
          <span>Call</span>
        </a>
      </div>
    </div>
  );
}

function DonorHelpModal({ open, section, onClose, onSelectSection, onBack }) {
  if (!open) return null;
  const active = section ? HELP_SECTIONS[section] : null;

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div
        className="modal modal--help"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="donor-help-title"
      >
        <div className="modal-header">
          <h3 id="donor-help-title">{active ? active.title : 'Help'}</h3>
          {!active ? (
            <p className="modal-header-sub">Policies and how to reach us.</p>
          ) : null}
        </div>
        <div className="modal-body help-modal-body">
          {!active ? (
            <ul className="help-menu-list">
              <li>
                <button type="button" className="help-menu-item" onClick={() => onSelectSection('privacy')}>
                  Privacy Policy
                </button>
              </li>
              <li>
                <button type="button" className="help-menu-item" onClick={() => onSelectSection('terms')}>
                  Terms of Use
                </button>
              </li>
              <li>
                <button type="button" className="help-menu-item" onClick={() => onSelectSection('contact')}>
                  Contact us
                </button>
              </li>
            </ul>
          ) : section === 'contact' ? (
            <ContactHelpContent />
          ) : (
            <div className="help-section-content">
              {active.paragraphs.map((paragraph) => (
                <p key={paragraph.slice(0, 48)}>{paragraph}</p>
              ))}
            </div>
          )}
        </div>
        <div className="modal-footer">
          {active ? (
            <button type="button" className="btn btn-secondary help-modal-back" onClick={onBack}>
              Back
            </button>
          ) : null}
          <button type="button" className="btn btn-success" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function DonorSiteFooter() {
  const year = new Date().getFullYear();
  return (
    <footer className="site-footer">
      <div className="site-footer-inner">
        <p className="site-footer-copy">
          © {year} DonorTrack. Nairobi, Kenya.
          <span className="site-footer-version">{APP_VERSION_LABEL}</span>
        </p>
      </div>
    </footer>
  );
}

/** Safaricom STK copy often says "still under processing" — use clearer donor-facing text. */
function mpesaUserNotice(message, fallback) {
  const text = String(message || '').trim();
  if (!text) return fallback;
  if (/processing|under process/i.test(text)) {
    return 'Enter your M-Pesa PIN on your phone to complete payment.';
  }
  return text;
}

export default function App() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const tokenRef = useRef(token);
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [showAuthGate, setShowAuthGate] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [authError, setAuthError] = useState('');
  const [authNotice, setAuthNotice] = useState('');
  const [authSubmitting, setAuthSubmitting] = useState(false);

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetLinkToken, setResetLinkToken] = useState('');
  const [resetPass, setResetPass] = useState('');
  const [resetPass2, setResetPass2] = useState('');
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regPassword2, setRegPassword2] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [donationAmount, setDonationAmount] = useState({});
  const [donationMethod, setDonationMethod] = useState({});
  const [donationPhone, setDonationPhone] = useState({});
  const [donatePhoneTouched, setDonatePhoneTouched] = useState({});
  const [platformSupportChoice, setPlatformSupportChoice] = useState({});
  const [platformSupportCustom, setPlatformSupportCustom] = useState({});
  const [mpesaNotice, setMpesaNotice] = useState('');
  const [donationSuccess, setDonationSuccess] = useState('');
  const [submittingId, setSubmittingId] = useState('');
  const [activeTab, setActiveTab] = useState('donate');
  const [donateSearchQuery, setDonateSearchQuery] = useState('');
  const [myGivingSearchQuery, setMyGivingSearchQuery] = useState('');
  const [myGivingStatusFilter, setMyGivingStatusFilter] = useState('all');
  const [updatesSearchQuery, setUpdatesSearchQuery] = useState('');
  const [showDonateModal, setShowDonateModal] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);
  const [selectedNeed, setSelectedNeed] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsBeneficiary, setDetailsBeneficiary] = useState(null);
  const [detailsNeed, setDetailsNeed] = useState(null);
  const [myDonations, setMyDonations] = useState([]);
  const [myDonationsLoading, setMyDonationsLoading] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const notificationsRef = useRef(null);
  const [paymentSummaryRequestId, setPaymentSummaryRequestId] = useState('');
  const [pendingDonate, setPendingDonate] = useState(null);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [paymentSummaryLoading, setPaymentSummaryLoading] = useState(false);
  const [paymentSummaryError, setPaymentSummaryError] = useState('');
  const [bookmarkedBeneficiaryIds, setBookmarkedBeneficiaryIds] = useState([]);
  const [activeShareKey, setActiveShareKey] = useState('');
  const [paymentUpdates, setPaymentUpdates] = useState([]);
  const [paymentUpdatesLoading, setPaymentUpdatesLoading] = useState(false);
  const [paymentUpdatesError, setPaymentUpdatesError] = useState('');
  const [wallet, setWallet] = useState({ balance: 0, currency: 'KES', transactions: [] });
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletError, setWalletError] = useState('');
  const [communities, setCommunities] = useState([]);
  const [communityStories, setCommunityStories] = useState([]);
  const [communitiesLoading, setCommunitiesLoading] = useState(false);
  const [communitiesError, setCommunitiesError] = useState('');
  const [communityFilter, setCommunityFilter] = useState('');
  const [activeStory, setActiveStory] = useState(null);
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState('idle');
  const [subscribeMessage, setSubscribeMessage] = useState('');
  const [blogStories, setBlogStories] = useState([]);
  const [blogNewsletters, setBlogNewsletters] = useState([]);
  const [blogLoading, setBlogLoading] = useState(false);
  const [blogError, setBlogError] = useState('');
  const [blogFilter, setBlogFilter] = useState('all');
  const [activeBlogItem, setActiveBlogItem] = useState(null);
  const [useWalletFunds, setUseWalletFunds] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpSection, setHelpSection] = useState(null);

  function openHelpModal(section = null) {
    setHelpSection(section);
    setShowHelpModal(true);
  }

  function closeHelpModal() {
    setShowHelpModal(false);
    setHelpSection(null);
  }

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    if (!showHelpModal) return undefined;
    function onKeyDown(e) {
      if (e.key === 'Escape') closeHelpModal();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showHelpModal]);

  useEffect(() => {
    if (!showDetailsModal) return undefined;
    function onKeyDown(e) {
      if (e.key === 'Escape') closeRequestDetails();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showDetailsModal]);

  useEffect(() => {
    if (!showNotifications) return undefined;
    function handlePointerDown(e) {
      if (notificationsRef.current && !notificationsRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [showNotifications]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let dirty = false;
    const rt = params.get('resetToken');
    if (rt) {
      setResetLinkToken(rt);
      setAuthMode('reset');
      setShowAuthGate(true);
      params.delete('resetToken');
      dirty = true;
    }
    if (params.get('emailVerified') === '1') {
      setAuthNotice('Your email is verified. You can log in below.');
      setAuthMode('login');
      setShowAuthGate(true);
      params.delete('emailVerified');
      dirty = true;
    }
    const paymentRequest = String(params.get('paymentRequest') || '').trim();
    if (paymentRequest) {
      setPaymentSummaryRequestId(paymentRequest);
    }
    const donateBeneficiary = String(params.get('beneficiary') || '').trim();
    const donateNeed = String(params.get('need') || '').trim();
    if (donateBeneficiary || donateNeed) {
      setPendingDonate({ beneficiaryId: donateBeneficiary, needId: donateNeed });
      params.delete('beneficiary');
      params.delete('need');
      dirty = true;
    }
    if (dirty) {
      const qs = params.toString();
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash || ''}`
      );
    }
  }, []);

  useEffect(() => {
    if (!paymentSummaryRequestId) return;
    let cancelled = false;
    (async () => {
      setPaymentSummaryLoading(true);
      setPaymentSummaryError('');
      try {
        const res = await fetch(
          `${API_BASE}/beneficiaries/needs/${encodeURIComponent(paymentSummaryRequestId)}/payment-summary`
        );
        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload.error || 'Could not load payment details');
        if (!cancelled) setPaymentSummary(payload);
      } catch (err) {
        if (!cancelled) setPaymentSummaryError(err.message);
      } finally {
        if (!cancelled) setPaymentSummaryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [paymentSummaryRequestId]);

  useEffect(() => {
    (async () => {
      const t = localStorage.getItem(TOKEN_KEY);
      if (!t) {
        setAuthReady(true);
        return;
      }
      setToken(t);
      try {
        const res = await fetch(`${API_BASE}/auth/me`, {
          headers: { Authorization: `Bearer ${t}` }
        });
        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload.error || 'Session expired');
        setUser(payload.user);
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        setToken('');
        setUser(null);
      } finally {
        setAuthReady(true);
      }
    })();
  }, []);

  const fetchJSON = useCallback(async (path, options = {}) => {
    const headers = { 'Content-Type': 'application/json', ...options.headers };
    const t = tokenRef.current;
    if (t) headers.Authorization = `Bearer ${t}`;
    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || `Request failed: ${response.status}`);
    return payload;
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const beneficiariesData = await fetchJSON('/beneficiaries');
      setBeneficiaries(beneficiariesData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [fetchJSON]);

  const loadNotifications = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      setUnreadNotificationCount(0);
      return;
    }
    try {
      const data = await fetchJSON('/notifications');
      setNotifications(data.notifications || []);
      setUnreadNotificationCount(Number(data.unreadCount) || 0);
    } catch (_) {
      setNotifications([]);
      setUnreadNotificationCount(0);
    }
  }, [fetchJSON, user]);

  useEffect(() => {
    if (!authReady) return;
    loadData();
  }, [authReady, loadData]);

  useEffect(() => {
    if (!user) return;
    loadNotifications();
  }, [user, loadNotifications]);

  useEffect(() => {
    if (!pendingDonate || !user) return;
    if (!Array.isArray(beneficiaries) || !beneficiaries.length) return;
    const { beneficiaryId, needId } = pendingDonate;
    let beneficiary = null;
    let need = null;
    if (beneficiaryId) {
      beneficiary = beneficiaries.find((b) => String(b.id) === String(beneficiaryId)) || null;
    }
    if (!beneficiary && needId) {
      beneficiary =
        beneficiaries.find((b) =>
          Array.isArray(b.needs) && b.needs.some((n) => String(n.id) === String(needId))
        ) || null;
    }
    if (!beneficiary) {
      setPendingDonate(null);
      return;
    }
    if (needId && Array.isArray(beneficiary.needs)) {
      need = beneficiary.needs.find((n) => String(n.id) === String(needId)) || null;
    }
    if (!need && Array.isArray(beneficiary.needs)) {
      need = beneficiary.needs[0] || null;
    }
    setSelectedBeneficiary(beneficiary);
    setSelectedNeed(need || null);
    setShowDonateModal(true);
    setActiveTab('donate');
    setPendingDonate(null);
  }, [pendingDonate, beneficiaries, user]);

  useEffect(() => {
    if (!user) return undefined;
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, [user, loadNotifications]);

  const loadMyDonations = useCallback(async () => {
    if (!user) {
      setMyDonations([]);
      setMyDonationsLoading(false);
      return;
    }
    setMyDonationsLoading(true);
    try {
      const donations = await fetchJSON('/donations');
      setMyDonations(Array.isArray(donations) ? donations : []);
    } catch (_) {
      setMyDonations([]);
    } finally {
      setMyDonationsLoading(false);
    }
  }, [fetchJSON, user]);

  useEffect(() => {
    loadMyDonations();
  }, [loadMyDonations]);

  useEffect(() => {
    if (activeTab === 'my-giving' && user) {
      loadMyDonations();
    }
  }, [activeTab, user, loadMyDonations]);

  const loadPaymentUpdates = useCallback(async () => {
    if (!user) {
      setPaymentUpdates([]);
      setPaymentUpdatesLoading(false);
      return;
    }
    setPaymentUpdatesLoading(true);
    setPaymentUpdatesError('');
    try {
      const rows = await fetchJSON('/donations/payment-updates');
      setPaymentUpdates(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setPaymentUpdatesError(err.message || 'Could not load updates');
      setPaymentUpdates([]);
    } finally {
      setPaymentUpdatesLoading(false);
    }
  }, [fetchJSON, user]);

  useEffect(() => {
    if (activeTab === 'updates' && user) {
      loadPaymentUpdates();
    }
  }, [activeTab, user, loadPaymentUpdates]);

  const loadWallet = useCallback(async () => {
    if (!user) {
      setWallet({ balance: 0, currency: 'KES', transactions: [] });
      setWalletLoading(false);
      return;
    }
    setWalletLoading(true);
    setWalletError('');
    try {
      const data = await fetchJSON('/wallet');
      setWallet({
        balance: Number(data.balance) || 0,
        currency: data.currency || 'KES',
        transactions: Array.isArray(data.transactions) ? data.transactions : []
      });
    } catch (err) {
      setWalletError(err.message || 'Could not load wallet');
      setWallet({ balance: 0, currency: 'KES', transactions: [] });
    } finally {
      setWalletLoading(false);
    }
  }, [fetchJSON, user]);

  useEffect(() => {
    loadWallet();
  }, [loadWallet]);

  useEffect(() => {
    if (activeTab === 'wallet' && user) {
      loadWallet();
    }
  }, [activeTab, user, loadWallet]);

  const loadCommunities = useCallback(async () => {
    setCommunitiesLoading(true);
    setCommunitiesError('');
    try {
      const data = await fetchJSON('/communities/stories');
      setCommunities(Array.isArray(data?.communities) ? data.communities : []);
      setCommunityStories(Array.isArray(data?.stories) ? data.stories : []);
    } catch (err) {
      setCommunitiesError(err.message || 'Could not load communities');
      setCommunities([]);
      setCommunityStories([]);
    } finally {
      setCommunitiesLoading(false);
    }
  }, [fetchJSON]);

  useEffect(() => {
    if (activeTab === 'communities') {
      loadCommunities();
    }
  }, [activeTab, loadCommunities]);

  const loadBlog = useCallback(async () => {
    setBlogLoading(true);
    setBlogError('');
    try {
      const [storiesData, newsletterData] = await Promise.all([
        fetchJSON('/communities/stories').catch(() => ({})),
        fetchJSON('/newsletter/published').catch(() => ({}))
      ]);
      setBlogStories(Array.isArray(storiesData?.stories) ? storiesData.stories : []);
      setBlogNewsletters(
        Array.isArray(newsletterData?.newsletters) ? newsletterData.newsletters : []
      );
    } catch (err) {
      setBlogError(err.message || 'Could not load the blog');
      setBlogStories([]);
      setBlogNewsletters([]);
    } finally {
      setBlogLoading(false);
    }
  }, [fetchJSON]);

  useEffect(() => {
    if (activeTab === 'blog') {
      loadBlog();
    }
  }, [activeTab, loadBlog]);

  async function handleNewsletterSubscribe(event) {
    event.preventDefault();
    const email = subscribeEmail.trim();
    if (!email) {
      setSubscribeStatus('error');
      setSubscribeMessage('Enter your email');
      return;
    }
    setSubscribeStatus('loading');
    setSubscribeMessage('');
    try {
      const res = await fetch(`${API_BASE}/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name: user?.name || null })
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload.error || 'Could not subscribe');
      setSubscribeStatus('success');
      setSubscribeMessage(payload.alreadySubscribed ? 'Already subscribed' : 'Subscribed!');
      setSubscribeEmail('');
      setTimeout(() => {
        setSubscribeStatus('idle');
        setSubscribeMessage('');
      }, 4000);
    } catch (err) {
      setSubscribeStatus('error');
      setSubscribeMessage(err.message || 'Could not subscribe');
    }
  }

  useEffect(() => {
    document.querySelectorAll('.donor-nav__link[data-route]').forEach((btn) => {
      const isActive = btn.dataset.route === activeTab;
      btn.classList.toggle('active', isActive);
      if (isActive) btn.setAttribute('aria-current', 'page');
      else btn.removeAttribute('aria-current');
    });
    const activeLink = document.querySelector('.donor-nav__link.active');
    if (activeLink && typeof activeLink.scrollIntoView === 'function') {
      activeLink.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [activeTab, bookmarkedBeneficiaryIds.length]);

  function openAuthGate(mode = 'login', notice = '') {
    setAuthMode(mode);
    setAuthError('');
    setAuthNotice(notice);
    setShowAuthGate(true);
  }

  function closeAuthGate() {
    if (authMode === 'reset') return;
    setShowAuthGate(false);
    setAuthError('');
    setAuthNotice('');
  }

  function navigateDonorTab(tab) {
    const authTabs = new Set(['my-giving', 'wallet', 'bookmarks', 'reminders', 'updates']);
    if (!user && authTabs.has(tab)) {
      openAuthGate('login', 'Sign in to open this section.');
      return;
    }
    setActiveTab(tab);
  }

  useEffect(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(BOOKMARKS_KEY) || '[]');
      setBookmarkedBeneficiaryIds(Array.isArray(raw) ? raw.map((v) => String(v)) : []);
    } catch {
      setBookmarkedBeneficiaryIds([]);
    }
  }, []);

  useEffect(() => {
    if (loading || !bookmarkedBeneficiaryIds.length) return;
    const pruned = pruneFullyFundedBookmarks(bookmarkedBeneficiaryIds, beneficiaries);
    if (bookmarkIdsEqual(pruned, bookmarkedBeneficiaryIds)) return;
    try {
      localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(pruned));
    } catch (_) {}
    setBookmarkedBeneficiaryIds(pruned);
  }, [beneficiaries, bookmarkedBeneficiaryIds, loading]);

  const categories = useMemo(() => {
    const set = new Set();
    for (const b of beneficiaries) {
      for (const need of b.needs || []) {
        if (need.category) set.add(need.category);
      }
    }
    return ['All', ...Array.from(set).sort()];
  }, [beneficiaries]);

  async function openNotifications() {
    const opening = !showNotifications;
    setShowNotifications(opening);
    if (opening) {
      try {
        await fetchJSON('/notifications/read-all', { method: 'PATCH' });
        const readAt = new Date().toISOString();
        setNotifications((prev) => prev.map((n) => ({ ...n, readAt: n.readAt || readAt })));
        setUnreadNotificationCount(0);
      } catch (_) {
        /* keep badge if mark-read fails */
      }
    }
  }

  const filteredBeneficiaries = useMemo(() => {
    return beneficiaries
      .map((b) => ({
        ...b,
        needs: (b.needs || []).filter(
          (need) => activeCategory === 'All' || need.category === activeCategory
        )
      }))
      .filter((b) => b.needs.length > 0);
  }, [beneficiaries, activeCategory]);

  const filteredNeedEntries = useMemo(
    () => flattenBeneficiaryNeeds(filteredBeneficiaries),
    [filteredBeneficiaries]
  );

  const searchFilteredNeedEntries = useMemo(
    () => filterNeedEntriesForDisplay(filteredNeedEntries, donateSearchQuery),
    [filteredNeedEntries, donateSearchQuery]
  );

  const visibleNeedEntries = useMemo(() => {
    if (activeTab !== 'bookmarks') return searchFilteredNeedEntries;
    const set = new Set(bookmarkedBeneficiaryIds.map((v) => String(v)));
    return searchFilteredNeedEntries.filter(({ beneficiary }) => set.has(String(beneficiary.id)));
  }, [activeTab, searchFilteredNeedEntries, bookmarkedBeneficiaryIds]);

  const donateCategoryCounts = useMemo(() => countNeedsByCategory(beneficiaries), [beneficiaries]);

  const donateUrgentCount = useMemo(
    () => searchFilteredNeedEntries.filter(({ need }) => need.urgent).length,
    [searchFilteredNeedEntries]
  );

  const filteredCommunityStories = useMemo(() => {
    if (!communityFilter) return communityStories;
    return communityStories.filter((s) => s.communityId === communityFilter);
  }, [communityStories, communityFilter]);

  const blogItems = useMemo(() => {
    const stories = (blogStories || []).map((s) => ({ ...s, kind: 'story' }));
    const newsletters = (blogNewsletters || []).map((n) => ({ ...n, kind: 'newsletter' }));
    const merged = [...stories, ...newsletters];
    merged.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    if (blogFilter === 'stories') return merged.filter((i) => i.kind === 'story');
    if (blogFilter === 'newsletters') return merged.filter((i) => i.kind === 'newsletter');
    return merged;
  }, [blogStories, blogNewsletters, blogFilter]);

  const bookmarkedSet = useMemo(
    () => new Set(bookmarkedBeneficiaryIds.map((v) => String(v))),
    [bookmarkedBeneficiaryIds]
  );

  const myGivingStatusCounts = useMemo(() => countMyDonationsByStatus(myDonations), [myDonations]);

  const myGivingStats = useMemo(() => {
    const total = myDonations.reduce((sum, d) => sum + Number(d.amount || 0), 0);
    const completed = myDonations.filter(
      (d) => String(d.status || '').toLowerCase() === 'completed'
    ).length;
    return {
      total,
      count: myDonations.length,
      completed
    };
  }, [myDonations]);

  const displayedMyDonations = useMemo(() => {
    const filtered = filterMyDonationsForDisplay(
      myDonations,
      myGivingSearchQuery,
      myGivingStatusFilter
    );
    return [...filtered].sort(
      (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
    );
  }, [myDonations, myGivingSearchQuery, myGivingStatusFilter]);

  const updatesStats = useMemo(() => {
    const withReceipt = paymentUpdates.filter(
      (u) => u.receiptFileName && u.receiptContentBase64
    ).length;
    return {
      count: paymentUpdates.length,
      withReceipt
    };
  }, [paymentUpdates]);

  const displayedPaymentUpdates = useMemo(() => {
    const sorted = [...paymentUpdates].sort(
      (a, b) => new Date(b.closedAt || 0) - new Date(a.closedAt || 0)
    );
    return filterPaymentUpdatesForDisplay(sorted, updatesSearchQuery);
  }, [paymentUpdates, updatesSearchQuery]);

  function openDonateModal(beneficiary, need) {
    if (!user) {
      openAuthGate('login', 'Sign in to contribute to a verified need.');
      return;
    }
    setDonationSuccess('');
    setError('');
    setSelectedBeneficiary(beneficiary);
    setSelectedNeed(need || null);
    setPlatformSupportChoice((prev) => ({ ...prev, [beneficiary.id]: 'none' }));
    setPlatformSupportCustom((prev) => ({ ...prev, [beneficiary.id]: '' }));
    setDonatePhoneTouched((prev) => ({ ...prev, [beneficiary.id]: false }));
    setUseWalletFunds(false);
    setShowDonateModal(true);
    setActiveTab('donate');
  }

  function openRequestDetails(beneficiary, need) {
    setActiveShareKey('');
    setDetailsBeneficiary(beneficiary);
    setDetailsNeed(need || null);
    setShowDetailsModal(true);
  }

  function closeRequestDetails() {
    setShowDetailsModal(false);
    setDetailsBeneficiary(null);
    setDetailsNeed(null);
  }

  function contributeFromDetails(beneficiary, need) {
    closeRequestDetails();
    openDonateModal(beneficiary, need);
  }

  const modalNeed = useMemo(() => {
    if (!selectedBeneficiary) return null;
    return selectedNeed || (selectedBeneficiary.needs || [])[0] || null;
  }, [selectedBeneficiary, selectedNeed]);

  const detailsActiveNeed = useMemo(() => {
    if (!detailsBeneficiary) return null;
    return detailsNeed || (detailsBeneficiary.needs || [])[0] || null;
  }, [detailsBeneficiary, detailsNeed]);

  const modalCheckout = useMemo(() => {
    if (!selectedBeneficiary) return null;
    return buildDonationCheckout({
      contribution: donationAmount[selectedBeneficiary.id],
      platformSupportChoice: platformSupportChoice[selectedBeneficiary.id] || 'none',
      platformSupportCustom: platformSupportCustom[selectedBeneficiary.id] || ''
    });
  }, [selectedBeneficiary, donationAmount, platformSupportChoice, platformSupportCustom]);

  const walletBalance = Number(wallet.balance || 0);

  // Estimated wallet amount applied toward the selected request: only the gap
  // the cash contribution doesn't cover, capped at the wallet balance.
  const walletApplyEstimate = useMemo(() => {
    if (!useWalletFunds || walletBalance <= 0 || !modalNeed || !modalCheckout) return 0;
    const remaining = needAmountRemaining(modalNeed);
    const gap = Math.max(0, remaining - Number(modalCheckout.contributionAmount || 0));
    return Math.min(walletBalance, Math.round(gap * 100) / 100);
  }, [useWalletFunds, walletBalance, modalNeed, modalCheckout]);

  async function submitLogin(e) {
    e.preventDefault();
    setAuthError('');
    setAuthNotice('');
    setAuthSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: loginEmail.trim().toLowerCase(),
          password: loginPassword
        })
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload.error || 'Login failed');
      localStorage.setItem(TOKEN_KEY, payload.token);
      setToken(payload.token);
      tokenRef.current = payload.token;
      setUser(payload.user);
      setLoginPassword('');
      setShowAuthGate(false);
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthSubmitting(false);
    }
  }

  async function submitForgot(e) {
    e.preventDefault();
    setAuthError('');
    setAuthNotice('');
    setAuthSubmitting(true);
    const ac = new AbortController();
    const abortTimer = setTimeout(() => ac.abort(), 60000);
    try {
      const res = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail.trim().toLowerCase() }),
        signal: ac.signal
      });
      clearTimeout(abortTimer);

      const payload = await Promise.race([
        res.json().catch(() => ({})),
        new Promise((resolve) => setTimeout(() => resolve({ __jsonTimedOut: true }), 20000))
      ]);

      setAuthSubmitting(false);

      if (!res.ok) throw new Error(payload.error || 'Request failed');

      setAuthNotice(
        payload.__jsonTimedOut
          ? 'Request completed slowly. If you have an account, check your email or try again.'
          : payload.message ||
              'If an account exists for that email, you will receive password reset instructions shortly.'
      );
      writeStoredResetEmail(forgotEmail);
      setAuthMode('login');
    } catch (err) {
      if (err.name === 'AbortError') {
        setAuthError('Request timed out. Try again in a moment.');
      } else {
        setAuthError(err.message);
      }
    } finally {
      clearTimeout(abortTimer);
      setAuthSubmitting(false);
    }
  }

  async function submitReset(e) {
    e.preventDefault();
    setAuthError('');
    if (resetPass !== resetPass2) {
      setAuthError('Passwords do not match');
      return;
    }
    if (resetPass.length < 8) {
      setAuthError('Password must be at least 8 characters');
      return;
    }
    if (!resetLinkToken) {
      setAuthError('Missing reset link. Open the link from your email.');
      return;
    }
    setAuthSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetLinkToken, password: resetPass })
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload.error || 'Could not reset password');
      setAuthNotice(payload.message || 'Your password has been updated. You can log in.');
      setAuthMode('login');
      writeStoredResetEmail('');
      setResetLinkToken('');
      setResetPass('');
      setResetPass2('');
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthSubmitting(false);
    }
  }

  async function submitRegister(e) {
    e.preventDefault();
    setAuthError('');
    if (regPassword !== regPassword2) {
      setAuthError('Passwords do not match');
      return;
    }
    if (regPassword.length < 8) {
      setAuthError('Password must be at least 8 characters');
      return;
    }
    setAuthSubmitting(true);
    const ac = new AbortController();
    const abortTimer = setTimeout(() => ac.abort(), 60000);
    try {
      const res = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: regName.trim(),
          email: regEmail.trim().toLowerCase(),
          password: regPassword
        }),
        signal: ac.signal
      });
      clearTimeout(abortTimer);

      const jsonTimeoutMs = 20000;
      const payload = await Promise.race([
        res.json().catch(() => ({})),
        new Promise((resolve) =>
          setTimeout(() => resolve({ __jsonTimedOut: true }), jsonTimeoutMs)
        )
      ]);

      setAuthSubmitting(false);

      if (!res.ok) {
        const fallback =
          res.status === 409
            ? 'An account with this email already exists. Try logging in, or use Forgot password if you were never verified.'
            : 'Could not create account';
        throw new Error(payload.error || fallback);
      }

      if (payload.token && payload.user) {
        localStorage.setItem(TOKEN_KEY, payload.token);
        setToken(payload.token);
        tokenRef.current = payload.token;
        setUser(payload.user);
        setRegPassword('');
        setRegPassword2('');
        setShowAuthGate(false);
        return;
      }

      if (res.status === 201 || res.status === 200) {
        setLoginEmail(regEmail.trim().toLowerCase());
        setAuthMode('login');
        if (payload.__jsonTimedOut) {
          setAuthNotice(
            'Account may have been created, but the response was slow. Check your email, or try logging in. If the account exists unverified, use the link from your inbox or request help.'
          );
        } else {
          const msg = payload.message || 'Check your email to verify your account.';
          if (payload.emailSent === true) {
            setAuthNotice(msg);
          } else if (payload.emailSent === false) {
            setAuthNotice(
              `${msg} We could not send the email (${payload.emailError || 'check SMTP on the API'}).`
            );
          } else {
            setAuthNotice(
              `${msg} If nothing arrives within a few minutes, check spam. The API sends mail in the background; fix SMTP on Render if delivery keeps failing.`
            );
          }
        }
        setRegPassword('');
        setRegPassword2('');
        return;
      }

      setAuthError('Unexpected response from server. If an account was created, try logging in.');
      setRegPassword('');
      setRegPassword2('');
    } catch (err) {
      if (err.name === 'AbortError') {
        setAuthError('Request timed out. If an account was created, try logging in or wait and try again.');
      } else {
        setAuthError(err.message);
      }
    } finally {
      clearTimeout(abortTimer);
      setAuthSubmitting(false);
    }
  }

  function handleLogout() {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    tokenRef.current = '';
    setUser(null);
    setMyDonations([]);
    setPaymentUpdates([]);
    setError('');
    setLoginEmail('');
    setLoginPassword('');
    setRegName('');
    setRegEmail('');
    setRegPassword('');
    setRegPassword2('');
    setForgotEmail('');
    setResetLinkToken('');
    setResetPass('');
    setResetPass2('');
    setAuthMode('login');
    setShowAuthGate(false);
    setActiveTab('donate');
    writeStoredResetEmail('');
  }

  function isMpesaPaymentComplete(payload) {
    if (!payload) return false;
    return payload.completed === true || payload.status === 'completed';
  }

  async function fetchDonationPaymentStatus(donationId) {
    if (!donationId) return null;
    return fetchJSON(`/mpesa/donation/${encodeURIComponent(donationId)}/status`);
  }

  async function fetchMpesaStatusFromDonationsList(donationId) {
    if (!donationId) return null;
    try {
      const donations = await fetchJSON('/donations');
      const row = donations.find((d) => d.id === donationId);
      if (row?.status === 'completed') {
        return {
          completed: true,
          status: 'completed',
          donationId,
          message: 'Payment received. Thank you for your donation!'
        };
      }
      if (row?.status === 'failed') {
        return { completed: false, status: 'failed', donationId, message: row.message || 'Payment failed' };
      }
    } catch (_) {
      /* ignore */
    }
    return null;
  }

  function nudgeMpesaPoll(checkoutRequestID) {
    const authToken = tokenRef.current;
    if (!authToken || !checkoutRequestID) return;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    fetch(`${API_BASE}/mpesa/poll/${encodeURIComponent(checkoutRequestID)}`, {
      headers: { Authorization: `Bearer ${authToken}` },
      signal: controller.signal
    })
      .catch(() => {})
      .finally(() => clearTimeout(timer));
  }

  async function waitForMpesaPaymentComplete(checkoutRequestID, donationId, onProgress) {
    nudgeMpesaPoll(checkoutRequestID);
    const maxAttempts = 120;
    for (let i = 0; i < maxAttempts; i += 1) {
      if (i > 0) await new Promise((r) => setTimeout(r, 1000));
      if (i > 0 && i % 10 === 0) nudgeMpesaPoll(checkoutRequestID);

      try {
        const statusCheck = await fetchDonationPaymentStatus(donationId);
        if (isMpesaPaymentComplete(statusCheck)) return statusCheck;
        if (statusCheck?.status === 'failed') {
          throw new Error(statusCheck.message || 'M-Pesa payment failed or was cancelled.');
        }
      } catch (statusErr) {
        if (
          statusErr.message &&
          (statusErr.message.includes('failed') || statusErr.message.includes('cancelled'))
        ) {
          throw statusErr;
        }
      }

      const fromList = await fetchMpesaStatusFromDonationsList(donationId);
      if (isMpesaPaymentComplete(fromList)) return fromList;
      if (fromList?.status === 'failed') {
        throw new Error(fromList.message || 'M-Pesa payment failed or was cancelled.');
      }

      if (onProgress) onProgress('Confirming your M-Pesa payment…');
    }

    const finalCheck = await fetchDonationPaymentStatus(donationId);
    if (isMpesaPaymentComplete(finalCheck)) return finalCheck;
    const finalFromList = await fetchMpesaStatusFromDonationsList(donationId);
    if (isMpesaPaymentComplete(finalFromList)) return finalFromList;

    throw new Error(
      'Payment confirmation is taking longer than expected. Check My giving in a moment.'
    );
  }

  async function refreshAfterDonation() {
    try {
      await loadData();
      await loadNotifications();
      await loadMyDonations();
    } catch (_) {
      /* modal already closed; refresh can fail without blocking success */
    }
  }

  function beneficiarySharePayload(beneficiary) {
    const id = String(beneficiary?.id || '').trim();
    const title = `Support ${beneficiary?.name || 'this request'} on DonorTrack`;
    const text = `${beneficiary?.name || 'A beneficiary'} needs support on DonorTrack.`;
    const url = `${window.location.origin}${window.location.pathname}?beneficiary=${encodeURIComponent(id)}`;
    return { title, text, url };
  }

  async function copyShareLink(beneficiary) {
    const { url } = beneficiarySharePayload(beneficiary);
    try {
      await navigator.clipboard.writeText(url);
      setDonationSuccess('Share link copied to clipboard.');
      setActiveShareKey('');
    } catch (_) {
      setError('Could not copy link right now. Please try again.');
    }
  }

  async function shareToWhatsApp(beneficiary) {
    const { text, url } = beneficiarySharePayload(beneficiary);
    const message = `${text} ${url}`;
    const target = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(target, '_blank', 'noopener,noreferrer');
    setActiveShareKey('');
  }

  async function shareToTwitter(beneficiary) {
    const { text, url } = beneficiarySharePayload(beneficiary);
    const target = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    window.open(target, '_blank', 'noopener,noreferrer');
    setActiveShareKey('');
  }

  async function shareToInstagram(beneficiary) {
    await copyShareLink(beneficiary);
    window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
    setActiveShareKey('');
  }

  function bookmarkBeneficiary(beneficiary) {
    try {
      const current = JSON.parse(localStorage.getItem(BOOKMARKS_KEY) || '[]');
      const id = String(beneficiary?.id || '').trim();
      if (!id) return;
      const next = Array.isArray(current) ? current : [];
      if (next.includes(id)) {
        const shouldRemove = window.confirm(
          `Remove ${beneficiary?.name || 'this request'} from bookmarks?`
        );
        if (!shouldRemove) return;
        const filtered = next.filter((v) => String(v) !== id);
        localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(filtered));
        setBookmarkedBeneficiaryIds(filtered.map((v) => String(v)));
        setDonationSuccess(`${beneficiary?.name || 'Beneficiary'} removed from bookmarks.`);
        return;
      }
      if (!next.includes(id)) {
        next.push(id);
        localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(next));
      }
      setBookmarkedBeneficiaryIds(next.map((v) => String(v)));
      setDonationSuccess(`${beneficiary?.name || 'Beneficiary'} bookmarked.`);
    } catch (_) {
      setError('Could not save bookmark right now.');
    }
  }

  async function tryCompleteMpesaAfterError(donationId, beneficiaryId, amount) {
    let statusCheck = await fetchDonationPaymentStatus(donationId);
    if (!isMpesaPaymentComplete(statusCheck)) {
      statusCheck = await fetchMpesaStatusFromDonationsList(donationId);
    }
    if (!isMpesaPaymentComplete(statusCheck)) return false;
    finishSuccessfulDonation(
      beneficiaryId,
      statusCheck.message ||
        `Thank you! Your donation of ${formatCurrency(amount)} was received successfully.`
    );
    await refreshAfterDonation();
    return true;
  }

  function finishSuccessfulDonation(beneficiaryId, successMessage) {
    setDonationAmount((prev) => ({ ...prev, [beneficiaryId]: '' }));
    setDonationPhone((prev) => ({ ...prev, [beneficiaryId]: '' }));
    setDonationMethod((prev) => ({ ...prev, [beneficiaryId]: 'Mpesa' }));
    setPlatformSupportChoice((prev) => ({ ...prev, [beneficiaryId]: 'none' }));
    setPlatformSupportCustom((prev) => ({ ...prev, [beneficiaryId]: '' }));
    setDonatePhoneTouched((prev) => ({ ...prev, [beneficiaryId]: false }));
    setMpesaNotice('');
    setError('');
    setShowDonateModal(false);
    setSelectedBeneficiary(null);
    setSelectedNeed(null);
    setDonationSuccess(successMessage);
  }

  function checkoutForBeneficiary(beneficiaryId) {
    return buildDonationCheckout({
      contribution: donationAmount[beneficiaryId],
      platformSupportChoice: platformSupportChoice[beneficiaryId] || 'none',
      platformSupportCustom: platformSupportCustom[beneficiaryId] || ''
    });
  }

  async function donate(beneficiaryId) {
    const paymentMethod = donationMethod[beneficiaryId] || 'Mpesa';
    const checkout = checkoutForBeneficiary(beneficiaryId);
    const needId = modalNeed?.id || null;
    const useWallet = useWalletFunds && Number(wallet.balance || 0) > 0;
    const message = `[${paymentMethod}]`;
    if (!user) {
      openAuthGate('login', 'Sign in to donate.');
      return;
    }
    if (!checkout || checkout.contributionAmount <= 0) {
      setError('Enter a valid donation amount.');
      return;
    }
    if (checkout.total <= 0) {
      setError('Enter a valid total amount.');
      return;
    }

    if (paymentMethod === 'Mpesa') {
      const rawPhone = donationPhone[beneficiaryId] || '';
      setDonatePhoneTouched((prev) => ({ ...prev, [beneficiaryId]: true }));
      const phoneError = mpesaPhoneValidationMessage(rawPhone);
      if (phoneError) {
        setError(phoneError);
        return;
      }
      const phoneNumber = formatMpesaPhoneNumber(rawPhone);
      setSubmittingId(beneficiaryId);
      setError('');
      setMpesaNotice('');
      setDonationSuccess('');
      let mpesaDonationId = null;
      let paymentHandled = false;
      let watchTimer = null;

      const handleMpesaSuccess = (result) => {
        if (paymentHandled) return;
        paymentHandled = true;
        if (watchTimer) clearInterval(watchTimer);
        const successMessage =
          result?.message ||
          `Thank you! Your payment of ${formatCurrency(checkout.total)} was received successfully.`;
        finishSuccessfulDonation(beneficiaryId, successMessage);
      };

      try {
        const stk = await fetchJSON('/mpesa/stk-push', {
          method: 'POST',
          body: JSON.stringify({
            beneficiaryId,
            needId,
            amount: checkout.total,
            contributionAmount: checkout.contributionAmount,
            platformSupportAmount: checkout.platformSupport,
            useWallet,
            phoneNumber
          })
        });
        mpesaDonationId = stk.donationId || null;
        const checkoutId = stk.checkoutRequestID || stk.CheckoutRequestID;
        if (!checkoutId) {
          throw new Error('M-Pesa did not return a checkout reference. Please try again.');
        }
        setMpesaNotice(
          mpesaUserNotice(
            stk.customerMessage,
            'STK push sent. Enter your M-Pesa PIN on your phone.'
          )
        );

        watchTimer = setInterval(async () => {
          if (paymentHandled || !mpesaDonationId) return;
          try {
            const statusCheck = await fetchDonationPaymentStatus(mpesaDonationId);
            if (isMpesaPaymentComplete(statusCheck)) {
              handleMpesaSuccess(statusCheck);
              void refreshAfterDonation();
              return;
            }
            const fromList = await fetchMpesaStatusFromDonationsList(mpesaDonationId);
            if (isMpesaPaymentComplete(fromList)) {
              handleMpesaSuccess(fromList);
              void refreshAfterDonation();
            }
          } catch (_) {
            /* keep watching */
          }
        }, 1000);

        const poll = await waitForMpesaPaymentComplete(checkoutId, mpesaDonationId, (msg) => {
          if (msg) setMpesaNotice(msg);
        });
        handleMpesaSuccess(poll);
        void refreshAfterDonation();
      } catch (err) {
        if (!paymentHandled && mpesaDonationId) {
          try {
            const recovered = await tryCompleteMpesaAfterError(
              mpesaDonationId,
              beneficiaryId,
              checkout.total
            );
            if (recovered) {
              paymentHandled = true;
              return;
            }
          } catch (_) {
            /* fall through to error message */
          }
        }
        if (!paymentHandled) {
          setError(err.message);
          setMpesaNotice('');
        }
      } finally {
        if (watchTimer) clearInterval(watchTimer);
        setSubmittingId('');
      }
      return;
    }

    setSubmittingId(beneficiaryId);
    setError('');
    setMpesaNotice('');
    setDonationSuccess('');
    try {
      await fetchJSON('/donations', {
        method: 'POST',
        body: JSON.stringify({
          beneficiaryId,
          needId,
          amount: checkout.total,
          contributionAmount: checkout.contributionAmount,
          platformSupportAmount: checkout.platformSupport,
          useWallet,
          message,
          paymentMethod
        })
      });
      finishSuccessfulDonation(
        beneficiaryId,
        `Thank you! Your donation of ${formatCurrency(checkout.total)} was recorded.`
      );
      await refreshAfterDonation();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmittingId('');
    }
  }

  if (!authReady) {
    return (
      <div className="app auth-container">
        <div className="spinner" aria-hidden />
        <p style={{ color: 'white', textAlign: 'center' }}>Loading…</p>
      </div>
    );
  }

  if (!user && showAuthGate) {
    return (
      <div className="app app--signed-out">
        <div className="signed-out-screen">
          <div className="auth-card">
            {authNotice ? <div className="success-message">{authNotice}</div> : null}
            {authMode === 'login' ? (
              <>
                <h2 className="auth-heading">Log in</h2>
                <p className="auth-sub">Use the email and password from your account.</p>
                {authError ? <div className="error-message">{authError}</div> : null}
                <form onSubmit={submitLogin}>
                  <div className="form-group">
                    <label htmlFor="login-email">Email</label>
                    <input
                      id="login-email"
                      name="username"
                      inputMode="email"
                      className="form-control"
                      type="email"
                      autoComplete="username"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="login-password">Password</label>
                    <PasswordInput
                      id="login-password"
                      autoComplete="current-password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      required
                    />
                    <div className="auth-inline-link">
                      <button
                        type="button"
                        className="link-button"
                        onClick={() => {
                          setForgotEmail(loginEmail);
                          setAuthMode('forgot');
                          setAuthError('');
                          setAuthNotice('');
                        }}
                      >
                        Forgot password?
                      </button>
                    </div>
                  </div>
                  <button className="btn btn-success" type="submit" disabled={authSubmitting} style={{ width: '100%' }}>
                    {authSubmitting ? 'Signing in…' : 'Log in'}
                  </button>
                </form>
                <div className="auth-switch">
                  New here?{' '}
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => {
                      setAuthMode('register');
                      setAuthError('');
                      setAuthNotice('');
                    }}
                  >
                    Create an account
                  </button>
                </div>
                {authMode !== 'reset' ? (
                  <div className="auth-switch">
                    <button type="button" className="link-button" onClick={closeAuthGate}>
                      Continue browsing
                    </button>
                  </div>
                ) : null}
              </>
            ) : authMode === 'forgot' ? (
              <>
                <h2 className="auth-heading">Reset password</h2>
                <p className="auth-sub">Enter your email and we will send you a link to choose a new password.</p>
                {authError ? <div className="error-message">{authError}</div> : null}
                <form onSubmit={submitForgot}>
                  <div className="form-group">
                    <label htmlFor="forgot-email">Email</label>
                    <input
                      id="forgot-email"
                      className="form-control"
                      type="email"
                      autoComplete="username email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                    />
                  </div>
                  <button className="btn btn-success" type="submit" disabled={authSubmitting} style={{ width: '100%' }}>
                    {authSubmitting ? 'Sending…' : 'Send reset link'}
                  </button>
                </form>
                <div className="auth-switch">
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => {
                      setAuthMode('login');
                      setAuthError('');
                    }}
                  >
                    Back to log in
                  </button>
                </div>
              </>
            ) : authMode === 'reset' ? (
              <>
                <h2 className="auth-heading">Choose a new password</h2>
                <p className="auth-sub">Enter and confirm your new password below.</p>
                {authError ? <div className="error-message">{authError}</div> : null}
                <form onSubmit={submitReset}>
                  <input
                    key={resetLinkToken || 'pending-reset'}
                    id="reset-account-email"
                    type="email"
                    name="username"
                    autoComplete="username"
                    readOnly
                    tabIndex={-1}
                    aria-hidden="true"
                    defaultValue={forgotEmail || loginEmail || readStoredResetEmail()}
                    style={{
                      position: 'absolute',
                      width: 1,
                      height: 1,
                      padding: 0,
                      margin: -1,
                      overflow: 'hidden',
                      clip: 'rect(0, 0, 0, 0)',
                      whiteSpace: 'nowrap',
                      border: 0
                    }}
                  />
                  <div className="form-group">
                    <label htmlFor="reset-pass">New password</label>
                    <PasswordInput
                      id="reset-pass"
                      autoComplete="new-password"
                      value={resetPass}
                      onChange={(e) => setResetPass(e.target.value)}
                      required
                      minLength={8}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="reset-pass2">Confirm new password</label>
                    <PasswordInput
                      id="reset-pass2"
                      autoComplete="new-password"
                      value={resetPass2}
                      onChange={(e) => setResetPass2(e.target.value)}
                      required
                    />
                  </div>
                  <button className="btn btn-success" type="submit" disabled={authSubmitting} style={{ width: '100%' }}>
                    {authSubmitting ? 'Updating…' : 'Update password'}
                  </button>
                </form>
                <div className="auth-switch">
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => {
                      setAuthMode('login');
                      setAuthError('');
                      setResetLinkToken('');
                      setResetPass('');
                      setResetPass2('');
                    }}
                  >
                    Back to log in
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="auth-heading">Create account</h2>
                <p className="auth-sub">Sign up as a donor to give to verified requests.</p>
                {authError ? <div className="error-message">{authError}</div> : null}
                <form onSubmit={submitRegister}>
                  <div className="form-group">
                    <label htmlFor="reg-name">Full name</label>
                    <input
                      id="reg-name"
                      className="form-control"
                      type="text"
                      autoComplete="name"
                      value={regName}
                      onChange={(e) => setRegName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="reg-email">Email</label>
                    <input
                      id="reg-email"
                      className="form-control"
                      type="email"
                      autoComplete="username email"
                      value={regEmail}
                      onChange={(e) => setRegEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="reg-password">Password</label>
                    <PasswordInput
                      id="reg-password"
                      autoComplete="new-password"
                      value={regPassword}
                      onChange={(e) => setRegPassword(e.target.value)}
                      required
                      minLength={8}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="reg-password2">Confirm password</label>
                    <PasswordInput
                      id="reg-password2"
                      autoComplete="new-password"
                      value={regPassword2}
                      onChange={(e) => setRegPassword2(e.target.value)}
                      required
                    />
                  </div>
                  <button className="btn btn-success" type="submit" disabled={authSubmitting} style={{ width: '100%' }}>
                    {authSubmitting ? 'Creating account…' : 'Sign up'}
                  </button>
                </form>
                <div className="auth-switch">
                  Already have an account?{' '}
                  <button type="button" className="link-button" onClick={() => { setAuthMode('login'); setAuthError(''); }}>
                    Log in
                  </button>
                </div>
                <div className="auth-switch">
                  <button type="button" className="link-button" onClick={closeAuthGate}>
                    Continue browsing
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        <DonorSiteFooter />
        <HelpFab onClick={() => openHelpModal()} />
        <DonorHelpModal
          open={showHelpModal}
          section={helpSection}
          onClose={closeHelpModal}
          onSelectSection={setHelpSection}
          onBack={() => setHelpSection(null)}
        />
      </div>
    );
  }

  return (
    <div className="app">
      <div className="donor-chrome">
        <header className="donor-header">
          <div className="donor-header__bar">
            <button
              type="button"
              className="donor-header__brand"
              onClick={() => navigateDonorTab('donate')}
              aria-label="DonorTrack — go to Donate"
            >
              <span className="donor-header__mark" aria-hidden="true">
                D
              </span>
              <span className="donor-header__titles">
                <span className="donor-header__name">DonorTrack</span>
                <span className="donor-header__tagline-wrap">
                  <span className="donor-header__tagline">A Record of Giving</span>
                </span>
              </span>
            </button>
            <div className="donor-header__actions">
              <form className="donor-subscribe" onSubmit={handleNewsletterSubscribe}>
                <input
                  type="email"
                  className="donor-subscribe__input"
                  placeholder="Email for newsletter"
                  aria-label="Email address for newsletter"
                  value={subscribeEmail}
                  onChange={(e) => setSubscribeEmail(e.target.value)}
                  disabled={subscribeStatus === 'loading'}
                />
                <button
                  type="submit"
                  className="donor-subscribe__btn"
                  disabled={subscribeStatus === 'loading'}
                >
                  {subscribeStatus === 'loading' ? '…' : 'Subscribe'}
                </button>
                {subscribeMessage ? (
                  <span
                    className={`donor-subscribe__msg${
                      subscribeStatus === 'error' ? ' donor-subscribe__msg--error' : ''
                    }`}
                    role="status"
                  >
                    {subscribeMessage}
                  </span>
                ) : null}
              </form>
              <div className="notifications-wrap" ref={notificationsRef}>
                {user ? (
                  <>
                    <button
                      type="button"
                      className="donor-header__icon-btn notification-bell"
                      aria-label={
                        unreadNotificationCount
                          ? `${unreadNotificationCount} unread notifications`
                          : 'Notifications'
                      }
                      aria-expanded={showNotifications}
                      aria-haspopup="true"
                      onClick={openNotifications}
                    >
                      <svg
                        className="notification-bell-icon"
                        viewBox="0 0 24 24"
                        width="22"
                        height="22"
                        aria-hidden
                      >
                        <path
                          fill="currentColor"
                          d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22Zm7-6V11a7 7 0 1 0-14 0v5l-2 2v1h18v-1l-2-2Z"
                        />
                      </svg>
                      {unreadNotificationCount > 0 ? (
                        <span className="notification-badge" aria-hidden>
                          {unreadNotificationCount > 9 ? '9+' : unreadNotificationCount}
                        </span>
                      ) : null}
                    </button>
                    {showNotifications ? (
                      <div className="notifications-panel" role="menu">
                        <div className="notifications-panel-header">
                          <strong>Notifications</strong>
                          {unreadNotificationCount > 0 ? (
                            <span className="notifications-panel-sub muted">
                              {unreadNotificationCount === 1
                                ? '1 unread'
                                : `${unreadNotificationCount} unread`}
                            </span>
                          ) : null}
                        </div>
                        {notifications.length ? (
                          <ul className="notifications-list">
                            {notifications.map((item) => (
                              <li
                                key={item.id}
                                className={`notification-item notification-${item.kind}${item.readAt ? '' : ' notification-unread'}`}
                              >
                                <div className="notification-item-title">{item.title}</div>
                                <div className="notification-item-body">{item.body}</div>
                                <div className="notification-item-time">{formatDate(item.createdAt)}</div>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="notifications-empty">No notifications yet.</p>
                        )}
                        <button
                          type="button"
                          className="notifications-view-all"
                          onClick={() => {
                            setShowNotifications(false);
                            navigateDonorTab('my-giving');
                          }}
                        >
                          View my giving
                        </button>
                      </div>
                    ) : null}
                  </>
                ) : null}
              </div>
              {user ? (
                <>
                  <span className="donor-header__user" title={user.email || undefined}>
                    {user.name}
                  </span>
                  <button type="button" className="donor-header__logout" onClick={handleLogout}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
                    </svg>
                    <span>Log out</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="donor-header__auth-link"
                    onClick={() => openAuthGate('login')}
                  >
                    Log in
                  </button>
                  <button
                    type="button"
                    className="donor-header__auth-link"
                    onClick={() => openAuthGate('register')}
                  >
                    Sign up
                  </button>
                </>
              )}
            </div>
          </div>
        </header>

        <nav className="donor-nav" id="donor-nav" aria-label="Main navigation">
          <div className="donor-nav__scroll">
            <div className="donor-nav__group" role="presentation">
              <button
                type="button"
                className={`donor-nav__link${activeTab === 'donate' ? ' active' : ''}`}
                data-route="donate"
                aria-current={activeTab === 'donate' ? 'page' : undefined}
                onClick={() => navigateDonorTab('donate')}
              >
                <span className="donor-nav__icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                </span>
                <span className="donor-nav__label">Donate</span>
              </button>
              <button
                type="button"
                className={`donor-nav__link${activeTab === 'communities' ? ' active' : ''}`}
                data-route="communities"
                aria-current={activeTab === 'communities' ? 'page' : undefined}
                onClick={() => navigateDonorTab('communities')}
              >
                <span className="donor-nav__icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </span>
                <span className="donor-nav__label">Communities</span>
              </button>
              <button
                type="button"
                className={`donor-nav__link${activeTab === 'blog' ? ' active' : ''}`}
                data-route="blog"
                aria-current={activeTab === 'blog' ? 'page' : undefined}
                onClick={() => navigateDonorTab('blog')}
              >
                <span className="donor-nav__icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16v16H4z" />
                    <path d="M4 8h16M8 12h8M8 16h5" />
                  </svg>
                </span>
                <span className="donor-nav__label">Blog</span>
              </button>
            </div>
            <div className="donor-nav__divider" aria-hidden="true" />
            <div className="donor-nav__group" role="presentation" aria-label="Your activity">
              <button
                type="button"
                className={`donor-nav__link${activeTab === 'my-giving' ? ' active' : ''}`}
                data-route="my-giving"
                aria-current={activeTab === 'my-giving' ? 'page' : undefined}
                onClick={() => navigateDonorTab('my-giving')}
              >
                <span className="donor-nav__icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="1" x2="12" y2="23" />
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  </svg>
                </span>
                <span className="donor-nav__label">My giving</span>
              </button>
              <button
                type="button"
                className={`donor-nav__link${activeTab === 'wallet' ? ' active' : ''}`}
                data-route="wallet"
                aria-current={activeTab === 'wallet' ? 'page' : undefined}
                onClick={() => navigateDonorTab('wallet')}
              >
                <span className="donor-nav__icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v1H5a2 2 0 0 0-2 2V7Z" />
                    <path d="M3 9h16a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9Z" />
                    <circle cx="16.5" cy="14" r="1.3" fill="currentColor" stroke="none" />
                  </svg>
                </span>
                <span className="donor-nav__label">My Wallet</span>
              </button>
              <button
                type="button"
                className={`donor-nav__link${activeTab === 'bookmarks' ? ' active' : ''}`}
                data-route="bookmarks"
                aria-current={activeTab === 'bookmarks' ? 'page' : undefined}
                onClick={() => navigateDonorTab('bookmarks')}
              >
                <span className="donor-nav__icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 3a2 2 0 0 0-2 2v16l8-4 8 4V5a2 2 0 0 0-2-2H6Z" />
                  </svg>
                </span>
                <span className="donor-nav__label">
                  Bookmarks
                  {bookmarkedBeneficiaryIds.length > 0 ? (
                    <span className="donor-nav__badge">{bookmarkedBeneficiaryIds.length}</span>
                  ) : null}
                </span>
              </button>
            </div>
            <div className="donor-nav__divider" aria-hidden="true" />
            <div className="donor-nav__group" role="presentation" aria-label="Stay informed">
              <button
                type="button"
                className={`donor-nav__link${activeTab === 'reminders' ? ' active' : ''}`}
                data-route="reminders"
                aria-current={activeTab === 'reminders' ? 'page' : undefined}
                onClick={() => navigateDonorTab('reminders')}
              >
                <span className="donor-nav__icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="13" r="8" />
                    <path d="M12 9v4l2 2M5 3 2 6M19 3l3 3M5 21l-2-3M19 21l2-3" />
                  </svg>
                </span>
                <span className="donor-nav__label">Reminders</span>
              </button>
              <button
                type="button"
                className={`donor-nav__link${activeTab === 'updates' ? ' active' : ''}`}
                data-route="updates"
                aria-current={activeTab === 'updates' ? 'page' : undefined}
                onClick={() => navigateDonorTab('updates')}
              >
                <span className="donor-nav__icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
                  </svg>
                </span>
                <span className="donor-nav__label">Updates</span>
              </button>
            </div>
          </div>
        </nav>
      </div>

      <main className="main-content">
        {paymentSummaryRequestId ? (
          <section className="updates-deep-link" aria-labelledby="updates-deep-link-title">
            <div className="updates-deep-link__card">
              <h2 id="updates-deep-link-title" className="updates-deep-link__title">
                Payment details
              </h2>
              <p className="updates-deep-link__lead muted">
                Shared link to verified payment information for a completed request.
              </p>
              {paymentSummaryLoading ? (
                <p className="muted">Loading payment details…</p>
              ) : null}
              {paymentSummaryError ? <div className="error-message">{paymentSummaryError}</div> : null}
              {!paymentSummaryLoading && paymentSummary ? (
                <PaymentDetailsTable summary={paymentSummary} />
              ) : null}
            </div>
          </section>
        ) : null}
        {donationSuccess ? <div className="success-message">{donationSuccess}</div> : null}
        {error && <div className="error-message">{error}</div>}
        {activeTab === 'wallet' ? (
          <section className="wallet-page">
            <header className="wallet-toolbar">
              <div className="wallet-toolbar__intro">
                <h2 className="wallet-toolbar__title">My Wallet</h2>
                <p className="wallet-toolbar__lead">
                  Your DonorTrack balance and a history of credits and debits.
                </p>
              </div>
              <button
                type="button"
                className="wallet-refresh-btn"
                onClick={loadWallet}
                disabled={walletLoading}
              >
                {walletLoading ? 'Refreshing…' : 'Refresh'}
              </button>
            </header>

            <div className="wallet-balance-card">
              <span className="wallet-balance-card__label">Wallet balance</span>
              <span className="wallet-balance-card__value">
                {formatCurrency(wallet.balance)}
              </span>
              <span className="wallet-balance-card__hint">
                Refunds for your donations are added here automatically.
              </span>
            </div>

            {walletError ? <div className="error-message">{walletError}</div> : null}

            <div className="wallet-transactions">
              <h3 className="wallet-transactions__title">Recent activity</h3>
              {walletLoading && !wallet.transactions.length ? (
                <p className="muted">Loading your wallet…</p>
              ) : wallet.transactions.length ? (
                <ul className="wallet-transactions__list">
                  {wallet.transactions.map((tx) => (
                    <li key={tx.id} className={`wallet-tx wallet-tx--${tx.type}`}>
                      <div className="wallet-tx__main">
                        <span className="wallet-tx__description">
                          {tx.description || (tx.type === 'credit' ? 'Wallet credit' : 'Wallet debit')}
                        </span>
                        <span className="wallet-tx__date">{formatDate(tx.createdAt)}</span>
                      </div>
                      <span className={`wallet-tx__amount wallet-tx__amount--${tx.type}`}>
                        {tx.type === 'debit' ? '-' : '+'}
                        {formatCurrency(tx.amount)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="empty-state donate-empty wallet-empty">
                  <p>No wallet activity yet. Refunds for your donations will show up here.</p>
                </div>
              )}
            </div>
          </section>
        ) : activeTab === 'my-giving' ? (
          <section className="my-giving-page">
            <header className="my-giving-toolbar">
              <div className="my-giving-toolbar__intro">
                <h2 className="my-giving-toolbar__title">My giving</h2>
                <p className="my-giving-toolbar__lead">
                  Your donation history, payment status, and amounts given on DonorTrack.
                </p>
              </div>
              <div className="my-giving-toolbar__actions">
                <label className="my-giving-search">
                  <span className="visually-hidden">Search your donations</span>
                  <input
                    type="search"
                    placeholder="Search beneficiary, location…"
                    value={myGivingSearchQuery}
                    onChange={(e) => setMyGivingSearchQuery(e.target.value)}
                    autoComplete="off"
                    disabled={myDonationsLoading || !myDonations.length}
                  />
                </label>
              </div>
            </header>

            {myDonationsLoading ? (
              <div className="empty-state donate-empty my-giving-empty">
                <h3>Loading your donations…</h3>
                <p className="muted">Fetching your giving history.</p>
              </div>
            ) : !myDonations.length ? (
              <div className="empty-state donate-empty my-giving-empty">
                <h3>No donations yet</h3>
                <p>When you support a request, your contributions will appear here with payment status.</p>
                <button
                  type="button"
                  className="btn btn-success my-giving-empty__action"
                  onClick={() => setActiveTab('donate')}
                >
                  Browse requests
                </button>
              </div>
            ) : (
              <>
                <div className="my-giving-stats" aria-label="Giving summary">
                  <div className="my-giving-stat my-giving-stat--primary">
                    <span className="my-giving-stat__label">Total given</span>
                    <strong className="my-giving-stat__value">{formatCurrency(myGivingStats.total)}</strong>
                  </div>
                  <div className="my-giving-stat">
                    <span className="my-giving-stat__label">Donations</span>
                    <strong className="my-giving-stat__value">{myGivingStats.count}</strong>
                  </div>
                  <div className="my-giving-stat">
                    <span className="my-giving-stat__label">Completed</span>
                    <strong className="my-giving-stat__value">{myGivingStats.completed}</strong>
                  </div>
                </div>

                <div className="my-giving-tabs-card">
                  <div className="my-giving-status-tabs" role="tablist" aria-label="Filter by status">
                    {[
                      { key: 'all', label: 'All' },
                      { key: 'completed', label: 'Completed' },
                      { key: 'pending', label: 'Pending' },
                      { key: 'failed', label: 'Failed' }
                    ].map(({ key, label }) => (
                      <button
                        key={key}
                        type="button"
                        role="tab"
                        aria-selected={myGivingStatusFilter === key}
                        className={`my-giving-status-tab${myGivingStatusFilter === key ? ' active' : ''}`}
                        onClick={() => setMyGivingStatusFilter(key)}
                      >
                        {label}
                        <span className="my-giving-status-tab__count">
                          ({myGivingStatusCounts[key] ?? 0})
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {myGivingSearchQuery.trim() &&
                displayedMyDonations.length > 0 &&
                displayedMyDonations.length <
                  myDonations.filter((d) => donationMatchesStatusFilter(d, myGivingStatusFilter))
                    .length ? (
                  <p className="my-giving-results-hint">
                    Showing {displayedMyDonations.length} matching donation
                    {displayedMyDonations.length === 1 ? '' : 's'} in this tab.
                  </p>
                ) : null}

                {!displayedMyDonations.length ? (
                  <div className="empty-state donate-empty my-giving-empty">
                    <h3>
                      {myGivingSearchQuery.trim()
                        ? 'No matching donations'
                        : myGivingStatusFilter !== 'all'
                          ? `No ${myGivingStatusFilter} donations`
                          : 'No donations to show'}
                    </h3>
                    <p>
                      {myGivingSearchQuery.trim()
                        ? 'Try another search or clear the filter.'
                        : 'Try another status tab above.'}
                    </p>
                    {myGivingSearchQuery.trim() ? (
                      <button
                        type="button"
                        className="btn btn-success my-giving-empty__action"
                        onClick={() => setMyGivingSearchQuery('')}
                      >
                        Clear search
                      </button>
                    ) : null}
                  </div>
                ) : (
                  <ul className="my-giving-list">
                    {displayedMyDonations.map((donation) => {
                      const beneficiary = donation.Beneficiary || donation.beneficiary || {};
                      const payment = formatPaymentMethodDisplay(parseDonationPaymentMethod(donation));
                      const meta = [beneficiary.location, beneficiary.category]
                        .filter(Boolean)
                        .join(' · ');
                      return (
                        <li key={donation.id}>
                          <article className="my-giving-card">
                            <header className="my-giving-card__header">
                              <div className="my-giving-card__identity">
                                <h3 className="my-giving-card__name">
                                  {beneficiary.name || 'Request'}
                                </h3>
                                {meta ? <p className="my-giving-card__meta muted">{meta}</p> : null}
                              </div>
                              <div className="my-giving-card__amount">
                                {formatCurrency(donation.amount)}
                              </div>
                            </header>
                            <footer className="my-giving-card__footer">
                              <span
                                className={`status-badge ${donationStatusBadgeClass(donation.status)}`}
                              >
                                {donationStatusLabel(donation.status)}
                              </span>
                              <span className="my-giving-card__payment">{payment}</span>
                              <time className="my-giving-card__date" dateTime={donation.createdAt || ''}>
                                {formatDate(donation.createdAt)}
                              </time>
                            </footer>
                          </article>
                        </li>
                      );
                    })}
                  </ul>
                )}

                {myGivingStats.completed > 0 ? (
                  <p className="my-giving-updates-hint muted">
                    Completed requests may include verified payment details on the{' '}
                    <button
                      type="button"
                      className="my-giving-inline-link"
                      onClick={() => setActiveTab('updates')}
                    >
                      Updates
                    </button>{' '}
                    tab.
                  </p>
                ) : null}
              </>
            )}
          </section>
        ) : activeTab === 'communities' ? (
          <section className="communities-page">
            <header className="communities-toolbar">
              <div className="communities-toolbar__intro">
                <h2 className="communities-toolbar__title">Communities</h2>
                <p className="communities-toolbar__lead">
                  Stories of impact from the communities we support together.
                </p>
              </div>
              <label className="communities-filter">
                <span className="visually-hidden">Filter by community</span>
                <select
                  value={communityFilter}
                  onChange={(e) => setCommunityFilter(e.target.value)}
                  disabled={communitiesLoading || !communities.length}
                >
                  <option value="">All communities</option>
                  {communities.map((community) => (
                    <option key={community.id} value={community.id}>
                      {community.name}
                    </option>
                  ))}
                </select>
              </label>
            </header>

            {communitiesError ? <div className="error-message">{communitiesError}</div> : null}

            {communitiesLoading ? (
              <div className="empty-state donate-empty communities-empty">
                <h3>Loading stories…</h3>
                <p className="muted">Fetching community stories.</p>
              </div>
            ) : !communityStories.length ? (
              <div className="empty-state donate-empty communities-empty">
                <h3>No stories yet</h3>
                <p>Community stories will appear here once they are published by DonorTrack.</p>
              </div>
            ) : !filteredCommunityStories.length ? (
              <div className="empty-state donate-empty communities-empty">
                <h3>No stories for this community</h3>
                <p>Choose a different community or view all communities.</p>
                <button
                  type="button"
                  className="btn btn-success communities-empty__action"
                  onClick={() => setCommunityFilter('')}
                >
                  View all communities
                </button>
              </div>
            ) : (
              <div className="communities-stories-grid">
                {filteredCommunityStories.map((story) => (
                  <button
                    type="button"
                    key={story.id}
                    className="community-story-tile"
                    onClick={() => setActiveStory(story)}
                  >
                    <span
                      className={`community-story-tile__badge community-story-tile__badge--${story.type}`}
                    >
                      {story.type === 'beneficiary' ? 'Beneficiary story' : 'Community story'}
                    </span>
                    <h3 className="community-story-tile__title">{story.title}</h3>
                    {story.subtitle ? (
                      <p className="community-story-tile__subtitle">{story.subtitle}</p>
                    ) : null}
                    <p className="community-story-tile__meta muted">
                      {[story.communityName, story.beneficiaryName].filter(Boolean).join(' · ')}
                    </p>
                    <p className="community-story-tile__preview">{storyPreviewText(story.body)}</p>
                    <span className="community-story-tile__more">Read story →</span>
                  </button>
                ))}
              </div>
            )}

            {activeStory ? (
              <div
                className="modal-overlay community-story-modal-overlay"
                role="dialog"
                aria-modal="true"
                aria-label={activeStory.title}
                onClick={() => setActiveStory(null)}
              >
                <div
                  className="community-story-modal"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    className="community-story-modal__close"
                    aria-label="Close story"
                    onClick={() => setActiveStory(null)}
                  >
                    ×
                  </button>
                  <span
                    className={`community-story-tile__badge community-story-tile__badge--${activeStory.type}`}
                  >
                    {activeStory.type === 'beneficiary' ? 'Beneficiary story' : 'Community story'}
                  </span>
                  <h3 className="community-story-modal__title">{activeStory.title}</h3>
                  {activeStory.subtitle ? (
                    <p className="community-story-modal__subtitle">{activeStory.subtitle}</p>
                  ) : null}
                  <p className="community-story-modal__meta muted">
                    {[activeStory.communityName, activeStory.beneficiaryName]
                      .filter(Boolean)
                      .join(' · ')}
                    {activeStory.createdAt
                      ? ` · ${new Date(activeStory.createdAt).toLocaleDateString()}`
                      : ''}
                  </p>
                  <div
                    className="community-story-modal__body"
                    dangerouslySetInnerHTML={{ __html: activeStory.body || '' }}
                  />
                </div>
              </div>
            ) : null}
          </section>
        ) : activeTab === 'blog' ? (
          <section className="communities-page blog-page">
            <header className="communities-toolbar">
              <div className="communities-toolbar__intro">
                <h2 className="communities-toolbar__title">Blog</h2>
                <p className="communities-toolbar__lead">
                  Stories of impact and newsletters from DonorTrack.
                </p>
              </div>
              <label className="communities-filter">
                <span className="visually-hidden">Filter blog</span>
                <select value={blogFilter} onChange={(e) => setBlogFilter(e.target.value)}>
                  <option value="all">All posts</option>
                  <option value="stories">Stories</option>
                  <option value="newsletters">Newsletters</option>
                </select>
              </label>
            </header>

            {blogError ? <div className="error-message">{blogError}</div> : null}

            {blogLoading ? (
              <div className="empty-state donate-empty communities-empty">
                <h3>Loading…</h3>
                <p className="muted">Fetching stories and newsletters.</p>
              </div>
            ) : !blogItems.length ? (
              <div className="empty-state donate-empty communities-empty">
                <h3>Nothing here yet</h3>
                <p>Stories and newsletters will appear here once DonorTrack publishes them.</p>
              </div>
            ) : (
              <div className="communities-stories-grid">
                {blogItems.map((item) => {
                  const isNewsletter = item.kind === 'newsletter';
                  const badgeType = isNewsletter ? 'newsletter' : item.type;
                  const badgeLabel = isNewsletter
                    ? 'Newsletter'
                    : item.type === 'beneficiary'
                      ? 'Beneficiary story'
                      : 'Community story';
                  return (
                    <button
                      type="button"
                      key={`${item.kind}-${item.id}`}
                      className="community-story-tile"
                      onClick={() => setActiveBlogItem(item)}
                    >
                      <span
                        className={`community-story-tile__badge community-story-tile__badge--${badgeType}`}
                      >
                        {badgeLabel}
                      </span>
                      <h3 className="community-story-tile__title">{item.title}</h3>
                      {item.subtitle ? (
                        <p className="community-story-tile__subtitle">{item.subtitle}</p>
                      ) : null}
                      <p className="community-story-tile__meta muted">
                        {[item.communityName, item.beneficiaryName].filter(Boolean).join(' · ')}
                      </p>
                      <p className="community-story-tile__preview">{storyPreviewText(item.body)}</p>
                      <span className="community-story-tile__more">Read more →</span>
                    </button>
                  );
                })}
              </div>
            )}

            {activeBlogItem ? (
              <div
                className="modal-overlay community-story-modal-overlay"
                role="dialog"
                aria-modal="true"
                aria-label={activeBlogItem.title}
                onClick={() => setActiveBlogItem(null)}
              >
                <div className="community-story-modal" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="community-story-modal__close"
                    aria-label="Close"
                    onClick={() => setActiveBlogItem(null)}
                  >
                    ×
                  </button>
                  <span
                    className={`community-story-tile__badge community-story-tile__badge--${
                      activeBlogItem.kind === 'newsletter' ? 'newsletter' : activeBlogItem.type
                    }`}
                  >
                    {activeBlogItem.kind === 'newsletter'
                      ? 'Newsletter'
                      : activeBlogItem.type === 'beneficiary'
                        ? 'Beneficiary story'
                        : 'Community story'}
                  </span>
                  <h3 className="community-story-modal__title">{activeBlogItem.title}</h3>
                  {activeBlogItem.subtitle ? (
                    <p className="community-story-modal__subtitle">{activeBlogItem.subtitle}</p>
                  ) : null}
                  <p className="community-story-modal__meta muted">
                    {[activeBlogItem.communityName, activeBlogItem.beneficiaryName]
                      .filter(Boolean)
                      .join(' · ')}
                    {activeBlogItem.createdAt
                      ? ` · ${new Date(activeBlogItem.createdAt).toLocaleDateString()}`
                      : ''}
                  </p>
                  <div
                    className="community-story-modal__body"
                    dangerouslySetInnerHTML={{ __html: activeBlogItem.body || '' }}
                  />
                </div>
              </div>
            ) : null}
          </section>
        ) : activeTab === 'updates' ? (
          <section className="updates-page">
            <header className="updates-toolbar">
              <div className="updates-toolbar__intro">
                <h2 className="updates-toolbar__title">Updates</h2>
                <p className="updates-toolbar__lead">
                  Verified payment details when requests you supported are completed and closed.
                </p>
              </div>
              <div className="updates-toolbar__actions">
                <label className="updates-search">
                  <span className="visually-hidden">Search payment updates</span>
                  <input
                    type="search"
                    placeholder="Search beneficiary, request…"
                    value={updatesSearchQuery}
                    onChange={(e) => setUpdatesSearchQuery(e.target.value)}
                    autoComplete="off"
                    disabled={paymentUpdatesLoading || !paymentUpdates.length}
                  />
                </label>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => setActiveTab('my-giving')}
                >
                  My giving
                </button>
              </div>
            </header>

            {paymentUpdatesError ? <div className="error-message">{paymentUpdatesError}</div> : null}

            {paymentUpdatesLoading ? (
              <div className="empty-state donate-empty updates-empty">
                <h3>Loading updates…</h3>
                <p className="muted">Fetching verified payment records.</p>
              </div>
            ) : !paymentUpdates.length ? (
              <div className="empty-state donate-empty updates-empty">
                <h3>No payment updates yet</h3>
                <p>
                  When a request you donated to is completed and closed, verified payment details
                  and receipts will appear here.
                </p>
                <button
                  type="button"
                  className="btn btn-success updates-empty__action"
                  onClick={() => setActiveTab('donate')}
                >
                  Browse requests
                </button>
              </div>
            ) : (
              <>
                <div className="updates-stats" aria-label="Updates summary">
                  <div className="updates-stat updates-stat--primary">
                    <span className="updates-stat__label">Completed requests</span>
                    <strong className="updates-stat__value">{updatesStats.count}</strong>
                  </div>
                  <div className="updates-stat">
                    <span className="updates-stat__label">With receipt</span>
                    <strong className="updates-stat__value">{updatesStats.withReceipt}</strong>
                  </div>
                </div>

                {updatesSearchQuery.trim() &&
                displayedPaymentUpdates.length > 0 &&
                displayedPaymentUpdates.length < paymentUpdates.length ? (
                  <p className="updates-results-hint">
                    Showing {displayedPaymentUpdates.length} of {paymentUpdates.length} updates
                    matching your search.
                  </p>
                ) : null}

                {!displayedPaymentUpdates.length ? (
                  <div className="empty-state donate-empty updates-empty">
                    <h3>No matching updates</h3>
                    <p>Try a different search term or clear the search box.</p>
                    <button
                      type="button"
                      className="btn btn-success updates-empty__action"
                      onClick={() => setUpdatesSearchQuery('')}
                    >
                      Clear search
                    </button>
                  </div>
                ) : (
                  <PaymentUpdatesList updates={displayedPaymentUpdates} />
                )}
              </>
            )}
          </section>
        ) : activeTab === 'reminders' ? (
          <RemindersTab
            beneficiaries={beneficiaries}
            user={user}
            fetchJSON={fetchJSON}
            onNotice={setDonationSuccess}
          />
        ) : (
          <section className={activeTab === 'donate' ? 'donate-page' : 'bookmarks-page'}>
            {activeTab === 'donate' ? (
              <header className="donate-toolbar">
                <div className="donate-toolbar__intro">
                  <h2 className="donate-toolbar__title">Support a request</h2>
                  <p className="donate-toolbar__lead">
                    Browse verified requests and contribute securely with M-Pesa, Airtel Money, or card.
                  </p>
                </div>
                <label className="donate-search">
                  <span className="visually-hidden">Search requests</span>
                  <input
                    type="search"
                    placeholder="Search name, location, category…"
                    value={donateSearchQuery}
                    onChange={(e) => setDonateSearchQuery(e.target.value)}
                    autoComplete="off"
                  />
                </label>
              </header>
            ) : (
              <header className="bookmarks-toolbar">
                <h2 className="donate-toolbar__title">Saved requests</h2>
                <p className="donate-toolbar__lead">
                  Requests you bookmarked to support later.
                </p>
              </header>
            )}

            {activeTab === 'donate' && user && Number(wallet.balance) > 0 ? (
              <button
                type="button"
                className="donate-wallet-banner"
                onClick={() => navigateDonorTab('wallet')}
              >
                <span className="donate-wallet-banner__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="6" width="20" height="13" rx="2" />
                    <path d="M2 10h20M16 14h2" />
                  </svg>
                </span>
                <span className="donate-wallet-banner__text">
                  <span className="donate-wallet-banner__label">Your wallet balance</span>
                  <span className="donate-wallet-banner__value">{formatCurrency(wallet.balance)}</span>
                </span>
                <span className="donate-wallet-banner__cta">View wallet</span>
              </button>
            ) : null}

            {activeTab === 'donate' && !loading && donateUrgentCount > 0 ? (
              <div className="donate-stats" aria-label="Request summary">
                <div className="donate-stat donate-stat--urgent">
                  <span className="donate-stat__label">Urgent</span>
                  <strong className="donate-stat__value">{donateUrgentCount}</strong>
                </div>
              </div>
            ) : null}

            {activeTab === 'donate' && !loading && categories.length > 1 ? (
              <div className="donate-filters-card">
                <span className="donate-filters__label">Filter by category</span>
                <div className="filter-tabs donate-filter-tabs" role="tablist" aria-label="Category">
                  {categories.map((category) => (
                    <button
                      key={category}
                      type="button"
                      role="tab"
                      aria-selected={activeCategory === category}
                      className={`filter-tab ${activeCategory === category ? 'active' : ''}`}
                      onClick={() => setActiveCategory(category)}
                    >
                      {category}
                      <span className="filter-tab__count">({donateCategoryCounts[category] || 0})</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {activeTab === 'donate' &&
            donateSearchQuery.trim() &&
            filteredNeedEntries.length > 0 &&
            visibleNeedEntries.length < searchFilteredNeedEntries.length ? (
              <p className="donate-results-hint">
                Showing {visibleNeedEntries.length} of {searchFilteredNeedEntries.length} requests matching
                your search.
              </p>
            ) : null}

            {loading ? (
              <div className="empty-state donate-empty">
                <h3>Loading requests…</h3>
                <p className="muted">Fetching verified requests you can support.</p>
              </div>
            ) : !visibleNeedEntries.length ? (
              <div className="empty-state donate-empty">
                <h3>
                  {activeTab === 'bookmarks'
                    ? 'No bookmarks yet'
                    : donateSearchQuery.trim()
                      ? 'No matching requests'
                      : activeCategory !== 'All'
                        ? `No ${activeCategory.toLowerCase()} requests`
                        : 'No verified requests yet'}
                </h3>
                <p>
                  {activeTab === 'bookmarks'
                    ? 'Tap the bookmark icon on a request card to save it here.'
                    : donateSearchQuery.trim()
                      ? 'Try a different search term or clear the search box.'
                      : activeCategory !== 'All'
                        ? 'Try another category above or choose All.'
                        : 'Approved requests will appear here once an admin verifies them.'}
                </p>
                {activeTab === 'donate' && donateSearchQuery.trim() ? (
                  <button
                    type="button"
                    className="btn btn-success"
                    style={{ marginTop: '1rem' }}
                    onClick={() => setDonateSearchQuery('')}
                  >
                    Clear search
                  </button>
                ) : null}
                {activeTab === 'bookmarks' && filteredBeneficiaries.length > 0 ? (
                  <button
                    type="button"
                    className="btn btn-success"
                    style={{ marginTop: '1rem' }}
                    onClick={() => setActiveTab('donate')}
                  >
                    Browse requests
                  </button>
                ) : null}
              </div>
            ) : (
              <section className="need-grid" aria-label="Requests to support">
                {visibleNeedEntries.map(({ beneficiary, need }) => {
                  const bookmarked = bookmarkedSet.has(String(beneficiary.id));
                  const shareKey = needShareKey(beneficiary.id, need.id);
                  const effectiveGoal = needEffectiveGoal(need);
                  const progress = needFundingProgress(need);
                  const pct = Math.round(progress);
                  return (
                    <article className="need-card donate-card" key={need.id}>
                      <div
                        className="need-card__body"
                        role="button"
                        tabIndex={0}
                        aria-label={`View details for ${beneficiary.name}'s request`}
                        onClick={() => openRequestDetails(beneficiary, need)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openRequestDetails(beneficiary, need);
                          }
                        }}
                      >
                      <header className="need-card__header">
                        <div className="beneficiary-avatar" aria-hidden="true">
                          {(beneficiary.name || '?')[0]}
                        </div>
                        <div className="need-card__identity">
                          <div className="beneficiary-name">{beneficiary.name}</div>
                          <div className="beneficiary-location">{beneficiary.location || '—'}</div>
                        </div>
                      </header>

                      <div className="need-card__tags">
                        <span
                          className={`category-tag category-${String(need.category || 'other').toLowerCase()}`}
                        >
                          {need.category}
                        </span>
                        {need.urgent ? <span className="urgent-badge">Urgent</span> : null}
                        {need.verified ? (
                          <span className="verified-badge verified-badge--need">Verified</span>
                        ) : null}
                        {(beneficiary.verified || beneficiary.status === 'active') ? (
                          <span className="verified-badge">Approved</span>
                        ) : null}
                        <span className="need-card__pct">{pct}% funded</span>
                      </div>

                      {need.description ? (
                        <div
                          className="need-card__desc"
                          dangerouslySetInnerHTML={{ __html: need.description }}
                        />
                      ) : null}

                      <div className="need-card__progress progress-section">
                        <div className="progress-bar-container">
                          <div className="progress-bar" style={{ width: `${progress}%` }} />
                        </div>
                        <div className="progress-info">
                          <span className="progress-amount">{formatCurrency(need.raisedAmount)} raised</span>
                          <span>Goal {formatCurrency(effectiveGoal)}</span>
                        </div>
                      </div>

                      <span className="need-card__details-hint" aria-hidden="true">
                        View details
                      </span>
                      </div>

                      <footer className="need-card__footer">
                        <div className="share-menu-wrap">
                          <button
                            type="button"
                            className="card-icon-btn"
                            aria-label={`Share request for ${beneficiary.name}`}
                            title="Share"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveShareKey((prev) => (prev === shareKey ? '' : shareKey));
                            }}
                          >
                            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                              <path
                                fill="currentColor"
                                d="M18 16a3 3 0 0 0-2.39 1.2L8.91 13.7a3.09 3.09 0 0 0 0-3.4l6.7-3.5A3 3 0 1 0 15 5a2.9 2.9 0 0 0 .09.7l-6.7 3.5a3 3 0 1 0 0 5.6l6.7 3.5A2.9 2.9 0 0 0 15 19a3 3 0 1 0 3-3Z"
                              />
                            </svg>
                          </button>
                          {activeShareKey === shareKey ? (
                            <div className="share-menu">
                              <button type="button" onClick={() => copyShareLink(beneficiary)}>
                                Copy link
                              </button>
                              <button type="button" onClick={() => shareToWhatsApp(beneficiary)}>
                                WhatsApp
                              </button>
                              <button type="button" onClick={() => shareToTwitter(beneficiary)}>
                                Twitter
                              </button>
                              <button type="button" onClick={() => shareToInstagram(beneficiary)}>
                                Instagram
                              </button>
                            </div>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          className="btn btn-success need-card__contribute"
                          onClick={() => openDonateModal(beneficiary, need)}
                        >
                          Contribute
                        </button>
                        <button
                          type="button"
                          className={`card-icon-btn${bookmarked ? ' card-icon-btn--active' : ''}`}
                          aria-label={
                            bookmarked
                              ? `Remove ${beneficiary.name} from bookmarks`
                              : `Bookmark ${beneficiary.name}`
                          }
                          aria-pressed={bookmarked}
                          title={bookmarked ? 'Bookmarked' : 'Bookmark'}
                          onClick={() => bookmarkBeneficiary(beneficiary)}
                        >
                          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                            <path
                              fill="currentColor"
                              d="M6 3a2 2 0 0 0-2 2v16l8-4 8 4V5a2 2 0 0 0-2-2H6Z"
                            />
                          </svg>
                        </button>
                      </footer>
                    </article>
                  );
                })}
              </section>
            )}
          </section>
        )}
      </main>

      {showDonateModal && selectedBeneficiary && modalCheckout ? (
        <div className="modal-overlay" onClick={() => setShowDonateModal(false)}>
          <div
            className="modal modal--donate"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="donate-modal-title"
          >
            <div className="modal-header modal-header--donate">
              <button
                type="button"
                className="modal-close"
                aria-label="Close"
                onClick={() => setShowDonateModal(false)}
              >
                ×
              </button>
              <h3 id="donate-modal-title">Contribute to {selectedBeneficiary.name}</h3>
              <p className="modal-header-sub">
                {[selectedBeneficiary.location || 'Kenya', modalNeed?.category]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              <div className="donate-modal-badges" aria-label="Verification">
                <span className="donate-modal-badge">Verified identity</span>
                <span className="donate-modal-badge">Reviewed request</span>
              </div>
              {modalNeed?.description ? (
                <div
                  className="modal-header-need muted"
                  dangerouslySetInnerHTML={{ __html: modalNeed.description }}
                />
              ) : null}
              {modalNeed && needAmountRemaining(modalNeed) > 0 ? (
                <p className="donate-modal-momentum muted">
                  {formatCurrency(needAmountRemaining(modalNeed))} remaining to fully fund this request
                </p>
              ) : null}
              {Number(selectedBeneficiary.supporterCount || 0) > 0 ? (
                <p className="donate-modal-momentum muted">
                  {selectedBeneficiary.supporterCount}{' '}
                  {selectedBeneficiary.supporterCount === 1 ? 'supporter has' : 'supporters have'} contributed
                </p>
              ) : null}
            </div>
            <div className="modal-body modal-body--donate">
              {walletBalance > 0 ? (
                <div className={`donate-wallet-option${useWalletFunds ? ' donate-wallet-option--active' : ''}`}>
                  <label className="donate-wallet-option__row">
                    <input
                      type="checkbox"
                      checked={useWalletFunds}
                      onChange={(e) => setUseWalletFunds(e.target.checked)}
                    />
                    <span className="donate-wallet-option__text">
                      <span className="donate-wallet-option__title">
                        Use my wallet balance
                      </span>
                      <span className="donate-wallet-option__balance">
                        {formatCurrency(walletBalance)} available
                      </span>
                    </span>
                  </label>
                  {useWalletFunds ? (
                    <p className="donate-wallet-option__note">
                      {walletApplyEstimate > 0
                        ? `${formatCurrency(walletApplyEstimate)} from your wallet will be applied to this request.`
                        : 'Your wallet will be applied toward this request if your contribution does not fully fund it.'}
                    </p>
                  ) : null}
                </div>
              ) : null}

              <div className="form-group">
                <label htmlFor="donate-amount-input">Contribution amount (KES)</label>
                <div className="donate-amount-quick">
                  {DONATE_QUICK_AMOUNTS.map((amt) => (
                    <button
                      key={amt}
                      type="button"
                      className={`donate-amount-chip${
                        Number(donationAmount[selectedBeneficiary.id]) === amt
                          ? ' donate-amount-chip--active'
                          : ''
                      }`}
                      onClick={() =>
                        setDonationAmount((prev) => ({
                          ...prev,
                          [selectedBeneficiary.id]: String(amt)
                        }))
                      }
                    >
                      {`Ksh ${amt.toLocaleString('en-KE')}`}
                    </button>
                  ))}
                </div>
                <input
                  id="donate-amount-input"
                  className="form-control"
                  type="number"
                  min="1"
                  step="1"
                  placeholder="Or enter custom amount"
                  value={donationAmount[selectedBeneficiary.id] || ''}
                  onChange={(e) =>
                    setDonationAmount((prev) => ({
                      ...prev,
                      [selectedBeneficiary.id]: e.target.value
                    }))
                  }
                />
              </div>

              <div className="form-group donate-support-section">
                <div className="donate-support-section__head">
                  <span className="form-group__label">Support DonorTrack (optional)</span>
                  <p className="donate-support-section__sub muted">
                    Help us verify requests, prevent fraud, and keep secure giving accessible.
                    This goes directly to DonorTrack and is not counted toward the request goal.
                  </p>
                </div>
                <div className="donate-amount-quick donate-support-chips">
                  {DONORTRACK_SUPPORT_CHIPS.map((chip) => (
                    <button
                      key={chip.id}
                      type="button"
                      className={`donate-amount-chip donate-support-chip${
                        (platformSupportChoice[selectedBeneficiary.id] || 'none') === chip.id
                          ? ' donate-amount-chip--active'
                          : ''
                      }`}
                      onClick={() =>
                        setPlatformSupportChoice((prev) => ({
                          ...prev,
                          [selectedBeneficiary.id]: chip.id
                        }))
                      }
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
                {(platformSupportChoice[selectedBeneficiary.id] || 'none') === 'custom' ? (
                  <input
                    className="form-control donate-support-custom"
                    type="number"
                    min="1"
                    step="1"
                    placeholder="Custom support amount (KES)"
                    value={platformSupportCustom[selectedBeneficiary.id] || ''}
                    onChange={(e) =>
                      setPlatformSupportCustom((prev) => ({
                        ...prev,
                        [selectedBeneficiary.id]: e.target.value
                      }))
                    }
                  />
                ) : null}
              </div>

              <div className="form-group payment-method-group">
                <span className="form-group__label" id="donate-payment-method-label">
                  Payment method
                </span>
                <div
                  className="payment-method-cards"
                  role="radiogroup"
                  aria-labelledby="donate-payment-method-label"
                >
                  {PAYMENT_METHOD_OPTIONS.map((method) => {
                    const active =
                      (donationMethod[selectedBeneficiary.id] || 'Mpesa') === method.id;
                    return (
                      <label
                        key={method.id}
                        className={`payment-method-card${active ? ' payment-method-card--active' : ''}`}
                        title={method.label}
                      >
                        <input
                          type="radio"
                          className="visually-hidden"
                          name={`payment-method-${selectedBeneficiary.id}`}
                          value={method.id}
                          checked={active}
                          aria-label={method.label}
                          onChange={(e) =>
                            setDonationMethod((prev) => ({
                              ...prev,
                              [selectedBeneficiary.id]: e.target.value
                            }))
                          }
                        />
                        <span className="payment-method-card__check" aria-hidden="true" />
                        <PaymentMethodMark methodId={method.id} />
                        <span className="payment-method-card__label">{method.shortLabel}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {(donationMethod[selectedBeneficiary.id] || 'Mpesa') === 'Mpesa' ? (
                <div className="form-group">
                  <label htmlFor="donate-phone-input">M-Pesa phone number</label>
                  <input
                    id="donate-phone-input"
                    className={`form-control donate-phone-input${
                      donatePhoneTouched[selectedBeneficiary.id] &&
                      mpesaPhoneValidationMessage(donationPhone[selectedBeneficiary.id] || '')
                        ? ' form-control--invalid'
                        : ''
                    }`}
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="0712 345 678 or +254 712 345 678"
                    value={formatKenyanPhoneDisplay(donationPhone[selectedBeneficiary.id] || '')}
                    onChange={(e) =>
                      setDonationPhone((prev) => ({
                        ...prev,
                        [selectedBeneficiary.id]: sanitizePhoneInput(e.target.value)
                      }))
                    }
                    onBlur={() =>
                      setDonatePhoneTouched((prev) => ({
                        ...prev,
                        [selectedBeneficiary.id]: true
                      }))
                    }
                    aria-invalid={
                      donatePhoneTouched[selectedBeneficiary.id] &&
                      Boolean(mpesaPhoneValidationMessage(donationPhone[selectedBeneficiary.id] || ''))
                    }
                  />
                  {donatePhoneTouched[selectedBeneficiary.id] &&
                  mpesaPhoneValidationMessage(donationPhone[selectedBeneficiary.id] || '') ? (
                    <p className="form-error" role="alert">
                      {mpesaPhoneValidationMessage(donationPhone[selectedBeneficiary.id] || '')}
                    </p>
                  ) : (
                    <p className="form-hint">
                      You will receive an STK push on this Safaricom number. We accept 07… and +254 7…
                      formats.
                    </p>
                  )}
                </div>
              ) : null}

              <div className="donate-payment-summary" aria-live="polite" aria-label="Payment summary">
                <div className="donate-payment-summary__row">
                  <span>Contribution</span>
                  <strong>{formatCurrency(modalCheckout.contributionAmount)}</strong>
                </div>
                {modalCheckout.platformSupport > 0 ? (
                  <div className="donate-payment-summary__row">
                    <span>Support DonorTrack <span className="muted">(to DonorTrack)</span></span>
                    <strong>{formatCurrency(modalCheckout.platformSupport)}</strong>
                  </div>
                ) : null}
                <div className="donate-payment-summary__row donate-payment-summary__row--total">
                  <span>Total to pay</span>
                  <strong>{formatCurrency(modalCheckout.total)}</strong>
                </div>
                {useWalletFunds && walletApplyEstimate > 0 ? (
                  <div className="donate-payment-summary__row donate-payment-summary__row--wallet">
                    <span>From wallet <span className="muted">(not charged)</span></span>
                    <strong>{formatCurrency(walletApplyEstimate)}</strong>
                  </div>
                ) : null}
              </div>

              <ul className="donate-trust-list" aria-label="Trust and security">
                <li>Verified request</li>
                <li>Secure M-Pesa payment</li>
                <li>Funds sent directly to beneficiary</li>
              </ul>

              {mpesaNotice ? <div className="success-message donate-modal-notice">{mpesaNotice}</div> : null}
            </div>
            <div className="modal-footer modal-footer--donate">
              <button type="button" className="btn btn-secondary" onClick={() => setShowDonateModal(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-success donate-modal-cta"
                disabled={submittingId === selectedBeneficiary.id}
                onClick={() => donate(selectedBeneficiary.id)}
              >
                {submittingId === selectedBeneficiary.id
                  ? (donationMethod[selectedBeneficiary.id] || 'Mpesa') === 'Mpesa'
                    ? 'Confirming payment…'
                    : 'Processing…'
                  : (donationMethod[selectedBeneficiary.id] || 'Mpesa') === 'Mpesa'
                    ? 'Pay with M-Pesa'
                    : 'Proceed with secure payment'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showDetailsModal && detailsBeneficiary && detailsActiveNeed ? (
        <div className="modal-overlay" onClick={closeRequestDetails}>
          <div
            className="modal modal--donate modal--details"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="request-details-title"
          >
            <div className="modal-header modal-header--donate">
              <button
                type="button"
                className="modal-close"
                aria-label="Close"
                onClick={closeRequestDetails}
              >
                ×
              </button>
              <div className="request-details__head">
                <div className="beneficiary-avatar beneficiary-avatar--lg" aria-hidden="true">
                  {(detailsBeneficiary.name || '?')[0]}
                </div>
                <div>
                  <h3 id="request-details-title">{detailsBeneficiary.name}</h3>
                  <p className="modal-header-sub">{detailsBeneficiary.location || 'Kenya'}</p>
                </div>
              </div>
              <div className="need-card__tags request-details__tags">
                <span
                  className={`category-tag category-${String(detailsActiveNeed.category || 'other').toLowerCase()}`}
                >
                  {detailsActiveNeed.category}
                </span>
                {detailsActiveNeed.urgent ? <span className="urgent-badge">Urgent</span> : null}
                {detailsActiveNeed.verified ? (
                  <span className="verified-badge verified-badge--need">Verified</span>
                ) : null}
                {detailsBeneficiary.verified || detailsBeneficiary.status === 'active' ? (
                  <span className="verified-badge">Approved</span>
                ) : null}
              </div>
            </div>

            <div className="modal-body modal-body--donate">
              {detailsActiveNeed.description ? (
                <div className="request-details__section">
                  <h4 className="request-details__label">About this request</h4>
                  <div
                    className="request-details__desc"
                    dangerouslySetInnerHTML={{ __html: detailsActiveNeed.description }}
                  />
                </div>
              ) : null}

              <div className="request-details__section">
                <div className="need-card__progress progress-section">
                  <div className="progress-bar-container">
                    <div
                      className="progress-bar"
                      style={{ width: `${needFundingProgress(detailsActiveNeed)}%` }}
                    />
                  </div>
                  <div className="progress-info">
                    <span className="progress-amount">
                      {formatCurrency(detailsActiveNeed.raisedAmount)} raised
                    </span>
                    <span>Goal {formatCurrency(needEffectiveGoal(detailsActiveNeed))}</span>
                  </div>
                </div>
                <ul className="request-details__stats">
                  <li>
                    <span>Funded</span>
                    <strong>{Math.round(needFundingProgress(detailsActiveNeed))}%</strong>
                  </li>
                  {needAmountRemaining(detailsActiveNeed) > 0 ? (
                    <li>
                      <span>Remaining</span>
                      <strong>{formatCurrency(needAmountRemaining(detailsActiveNeed))}</strong>
                    </li>
                  ) : null}
                  {Number(detailsBeneficiary.supporterCount || 0) > 0 ? (
                    <li>
                      <span>Supporters</span>
                      <strong>{detailsBeneficiary.supporterCount}</strong>
                    </li>
                  ) : null}
                  {detailsBeneficiary.createdAt ? (
                    <li>
                      <span>Listed</span>
                      <strong>{new Date(detailsBeneficiary.createdAt).toLocaleDateString()}</strong>
                    </li>
                  ) : null}
                </ul>
              </div>

              {Array.isArray(detailsBeneficiary.needs) && detailsBeneficiary.needs.length > 1 ? (
                <div className="request-details__section">
                  <h4 className="request-details__label">
                    Other requests from {detailsBeneficiary.name}
                  </h4>
                  <ul className="request-details__needs">
                    {detailsBeneficiary.needs.map((n) => (
                      <li key={n.id}>
                        <button
                          type="button"
                          className={`request-details__need${
                            n.id === detailsActiveNeed.id ? ' is-active' : ''
                          }`}
                          onClick={() => setDetailsNeed(n)}
                        >
                          <span className="request-details__need-cat">
                            {n.category}
                            {n.urgent ? ' · Urgent' : ''}
                          </span>
                          <span className="request-details__need-amt">
                            {Math.round(needFundingProgress(n))}% · Goal{' '}
                            {formatCurrency(needEffectiveGoal(n))}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>

            <div className="modal-footer request-details__footer">
              <button
                type="button"
                className={`card-icon-btn${
                  bookmarkedSet.has(String(detailsBeneficiary.id)) ? ' card-icon-btn--active' : ''
                }`}
                aria-label={
                  bookmarkedSet.has(String(detailsBeneficiary.id))
                    ? `Remove ${detailsBeneficiary.name} from bookmarks`
                    : `Bookmark ${detailsBeneficiary.name}`
                }
                aria-pressed={bookmarkedSet.has(String(detailsBeneficiary.id))}
                title={
                  bookmarkedSet.has(String(detailsBeneficiary.id)) ? 'Bookmarked' : 'Bookmark'
                }
                onClick={() => bookmarkBeneficiary(detailsBeneficiary)}
              >
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M6 3a2 2 0 0 0-2 2v16l8-4 8 4V5a2 2 0 0 0-2-2H6Z"
                  />
                </svg>
              </button>
              <button type="button" className="btn btn-secondary" onClick={closeRequestDetails}>
                Close
              </button>
              <button
                type="button"
                className="btn btn-success donate-modal-cta"
                onClick={() => contributeFromDetails(detailsBeneficiary, detailsActiveNeed)}
              >
                Contribute
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <DonorSiteFooter />
      <HelpFab onClick={() => openHelpModal()} />
      <DonorHelpModal
        open={showHelpModal}
        section={helpSection}
        onClose={closeHelpModal}
        onSelectSection={setHelpSection}
        onBack={() => setHelpSection(null)}
      />
    </div>
  );
}
